import { useState, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight, ChevronDown, User, Users, Calendar, MapPin, Clock, Package, Plus, X, Phone, CreditCard, Truck, CalendarClock, Search, Download, PenLine, Copy, Building2, Lock } from 'lucide-react';
import { addDays, subDays, format, startOfWeek, getWeek, getYear, setWeek, startOfYear } from 'date-fns';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';
import { supabase, canSeeDispatchMoney, DISPATCH_MONEY_ROLE_LABELS } from '../../lib/supabase';
import { fetchAllRows } from '../../lib/fetchAllRows';
import { openStoredFile } from '../../utils/fileUrls';

// Company (carrier authority) badge colors keyed by companies.code — same
// colors the Excel dispatch board uses. Companies outside this map fall back to
// the palette below (indexed by their position in the companies list) so a new
// authority still gets a stable color.
// Keys are the code/name with spaces and punctuation stripped. Colors must stay
// readable on every load-cell background (green loaded, yellow dispatched, red
// empty...), so they're all dark — a light badge disappears on a green cell.
const COMPANY_COLORS: Record<string, string> = {
  APOLLO: '#B3031B',
  TESLA: '#111429',
  TMARK: '#006172',
  CSNATIONAL: '#1E1B4B',
  CSN: '#1E1B4B',
  CS: '#1E1B4B',
};
const COMPANY_FALLBACK_COLORS = ['#1E1B4B', '#7C2D12', '#4C1D95', '#0F172A', '#831843'];

const formatDateTimeNoTZ = (dateString: string) => {
  if (!dateString) return 'N/A';
  const parts = dateString.includes('T') 
    ? dateString.split('T')[0].split('-').concat(dateString.split('T')[1].split(':'))
    : dateString.split(/[-T: ]/);
  
  const year = parseInt(parts[0]);
  const month = parseInt(parts[1]);
  const day = parseInt(parts[2]);
  const hour = parts[3] ? parseInt(parts[3]) : 0;
  const minute = parts[4] ? parseInt(parts[4]) : 0;
  
  const hour12 = hour % 12 || 12;
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const minuteStr = String(minute).padStart(2, '0');
  
  return `${month}/${day}, ${hour12}:${minuteStr} ${ampm}`;
};

const formatTimeOnly = (timeString: string) => {
  if (!timeString) return '';
  const parts = timeString.split(':');
  const hour = parseInt(parts[0]);
  const minute = parseInt(parts[1]);
  
  const hour12 = hour % 12 || 12;
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const minuteStr = String(minute).padStart(2, '0');
  
  return `${hour12}:${minuteStr} ${ampm}`;
};

const formatDateTimeWithWindow = (date: string, timeFrom: string, timeTo?: string) => {
  if (!date || !timeFrom) return 'N/A';
  
  const dateTimeFrom = formatDateTimeNoTZ(`${date.split('T')[0]}T${timeFrom}`);
  
  if (timeTo) {
    const timeToFormatted = formatTimeOnly(timeTo);
    // Extract just the time part from dateTimeFrom and replace with range
    const datePart = dateTimeFrom.split(', ')[0]; // "2/2"
    const timeFromFormatted = formatTimeOnly(timeFrom);
    return `${datePart}, ${timeFromFormatted} - ${timeToFormatted}`;
  }
  
  return dateTimeFrom;
};

interface Driver {
  id: string;
  first_name: string;
  last_name: string;
  truck_number?: string;
  truck_id?: string;
  phone?: string;
  status: string;
  driver_status?: string;
  termination_date?: string | null;
  dispatcher: string;
  driver_type?: string;
  cdl_number?: string;
  cdl_expiration?: string;
  cdl_state?: string;
  cdl_image_url?: string;
  twic_image_url?: string;
  truck_make?: string;
  truck_model?: string;
  truck_year?: number;
  trailer_number?: string;
  trailer_id?: string;
  trailer_make?: string;
  trailer_year?: number;
  trailer_type?: string;
  needs_office_visit?: boolean;
  office_visit_reason?: string;
  dispatcher_id?: string;
  // When a driver drove loads for more than one dispatcher in the same week we
  // emit one board row per dispatcher. rowKey makes the row unique and
  // attributedDispatchIds limits the row to the loads driven for THAT
  // dispatcher. When undefined the row shows all of the driver's dispatches
  // (idle drivers / snapshot rows).
  rowKey?: string;
  attributedDispatchIds?: string[];
  // Team drivers: when this row represents a 2-driver team (the team's primary
  // member carries the team's loads), team_label holds both names for display.
  team_id?: string;
  team_label?: string;
}

interface LoadStop {
  city: string;
  state: string;
  stop_type: string;
  stop_order: number;
  date: string;
  time_from: string;
  time_to?: string;
  company_name?: string;
}

interface LoadNote {
  id: string;
  note: string;
  created_at: string;
  created_by: string;
  profiles?: {
    full_name: string;
  };
}

interface Dispatch {
  id: string;
  driver_id: string;
  truck_id: string;
  start_time: string;
  end_time: string;
  status: string;
  load_id?: string;
  load_number?: string;
  dispatch_number?: string;
  origin?: string;
  destination?: string;
  load_stops?: LoadStop[];
  load_notes?: LoadNote[];
  stops?: any[];
  is_rescheduled?: boolean;
  carrier?: string;
  total_rate?: number;
  // loads.total_miles is the LOADED mileage only, despite the name; the empty
  // (deadhead) mileage lives on the dispatch row as empty_miles. The true total
  // for the load is the two added together — same split LoadPreviewModal shows.
  total_miles?: number;
  empty_miles?: number;
  // True once an invoice PDF has actually been generated and uploaded for this
  // load (loads.invoice_sent, written by ReadyForBillingView). This is the
  // app's own definition of "invoiced" — it's the flag that splits Ready for
  // Billing's Ready tab from its Invoices tab.
  // Deliberately NOT ready_for_billing / billing_sent_at: those are set by
  // "Send to Accounting", which every delivered load is meant to pass through,
  // so they mark "queued for billing", not "invoiced" — the distinction the
  // board is being asked to show.
  invoice_sent?: boolean;
  // Driver's pay for this load (dispatches.driver_pay) — shown next to the
  // load's gross rate in the weekly TOTAL column.
  driver_pay?: number;
  // Raw truck UUID + load creator, used to resolve the historical dispatcher
  // this load was driven for (matches DispatchersGrossView attribution).
  raw_truck_id?: string;
  truck_dispatcher_id?: string;
  load_created_by?: string;
  // Resolved historical dispatcher id for this dispatch (filled in loadData).
  resolved_dispatcher_id?: string;
}

// Index an equipment_assignments timeline by driver for fast lookup.
function buildTimelineByDriver(rows: any[]): Map<string, any[]> {
  const map = new Map<string, any[]>();
  (rows || []).forEach((e: any) => {
    if (!e.driver_id) return;
    const list = map.get(e.driver_id) || [];
    list.push(e);
    map.set(e.driver_id, list);
  });
  return map;
}

// Resolve which dispatcher a single dispatch counts toward. Mirrors
// DispatchersGrossView.resolveHistoricalDispatcherId so the board's grouping
// matches the money view exactly:
//   1) who booked the load (loads.created_by),
//   2) the dispatcher of the truck the driver was assigned to at dispatch time,
//   3) the truck's current dispatcher.
// Returns a dispatcher UUID or the sentinel 'unassigned'.
function resolveDispatchDispatcherId(dispatch: Dispatch, timelineByDriver: Map<string, any[]>): string {
  if (dispatch.load_created_by) return dispatch.load_created_by;
  const ts = new Date(dispatch.start_time).getTime();
  const active = (timelineByDriver.get(dispatch.driver_id) || []).filter((e: any) => {
    const a = new Date(e.assigned_at).getTime();
    const r = e.released_at ? new Date(e.released_at).getTime() : null;
    return a <= ts && (r === null || ts < r);
  });
  const matched = active.find((e: any) => e.truck_id === dispatch.raw_truck_id) || active[0];
  return matched?.trucks?.dispatcher_id || dispatch.truck_dispatcher_id || 'unassigned';
}

export function DispatchBoardView() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [dispatches, setDispatches] = useState<Dispatch[]>([]);
  // Team drivers: map every member driver_id -> its active team. primaryId is the
  // member whose driver_id all team dispatches are normalized to (so the team's
  // loads aggregate on one board row). label is both names for display.
  const [teamsByDriverId, setTeamsByDriverId] = useState<Map<string, { teamId: string; primaryId: string; label: string; memberIds: string[] }>>(new Map());
  const [dispatchers, setDispatchers] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState<Driver | null>(null);
  const [availableLoads, setAvailableLoads] = useState<any[]>([]);
  const [loadSearch, setLoadSearch] = useState('');
  const [assigningLoad, setAssigningLoad] = useState(false);
  const [driverPay, setDriverPay] = useState<number>(0);
  const [selectedDriverType, setSelectedDriverType] = useState<string | null>(null);
  const [selectedLoad, setSelectedLoad] = useState<any | null>(null);
  const [collapsedDispatchers, setCollapsedDispatchers] = useState<Set<string>>(new Set());
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [selectedWeek, setSelectedWeek] = useState(getWeek(new Date(), { weekStartsOn: 1 }));
  const [selectedYear, setSelectedYear] = useState(getYear(new Date()));
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [hoveredDriver, setHoveredDriver] = useState<string | null>(null);
  const [pinnedDriver, setPinnedDriver] = useState<string | null>(null);
  const [hoveredLoadId, setHoveredLoadId] = useState<string | null>(null);
  // Load card the board search just jumped to. Cleared on a timer so the
  // highlight is a hint, not a second selection state.
  const [highlightedDispatchId, setHighlightedDispatchId] = useState<string | null>(null);
  const highlightTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [hoverPosition, setHoverPosition] = useState({ x: 0, y: 0, isLeft: false, isAbove: false });
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [selectedDispatch, setSelectedDispatch] = useState<Dispatch | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<string>('');
  const [statusNote, setStatusNote] = useState<string>('');
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const datePickerRef = useRef<HTMLDivElement>(null);
  const [selectedDay, setSelectedDay] = useState<Date | null>(null);
  const [selectedDriverForNote, setSelectedDriverForNote] = useState<Driver | null>(null);
  const [driverLocationCity, setDriverLocationCity] = useState<string>('');
  const [driverSearchQuery, setDriverSearchQuery] = useState<string>('');
  const [showCompletionConfirm, setShowCompletionConfirm] = useState(false);
  const [dispatchersList, setDispatchersList] = useState<{ id: string; full_name: string }[]>([]);
  const [reassigningDriverId, setReassigningDriverId] = useState<string | null>(null);
  const [reassigningLoad, setReassigningLoad] = useState(false);
  const [showReassignModal, setShowReassignModal] = useState(false);
  const [reassignSearch, setReassignSearch] = useState('');
  const [reassignTargetDriverId, setReassignTargetDriverId] = useState<string | null>(null);
  // Company (carrier authority) filter. Empty set = show every company.
  const [companies, setCompanies] = useState<{ id: string; name: string; code: string }[]>([]);
  const [selectedCompanyIds, setSelectedCompanyIds] = useState<Set<string>>(new Set());
  const [showCompanyFilter, setShowCompanyFilter] = useState(false);
  const [companyByDriverId, setCompanyByDriverId] = useState<Map<string, string>>(new Map());
  const companyFilterRef = useRef<HTMLDivElement>(null);
  const toast = useToast();
  const { profile } = useAuth();
  // Load rates, driver pay, gross and RPM are only for admins and accounting.
  // Everyone else (dispatch, safety, maintenance, ELD, ...) sees the board without
  // any dollar figures; the weekly TOTAL column is dropped from the grid for them.
  const canSeeMoney = canSeeDispatchMoney(profile?.role);
  const boardGridColumns = canSeeMoney ? '200px repeat(8, 1fr) 92px' : '200px repeat(8, 1fr)';
  const driverTotalsGridCols = canSeeMoney ? 'grid-cols-[1.6fr_1fr_1fr_1fr]' : 'grid-cols-[1.6fr_1fr]';
  // Shown under the dollar figures in the board's hover popups (same notice as on
  // Dispatchers Gross), so the people who see the money know who else does.
  const moneyVisibilityNote = `Dollar amounts visible only to ${DISPATCH_MONEY_ROLE_LABELS.join(', ')}. Hidden from everyone else.`;

  const getStatusColor = (status: string) => {
    // Modern, vibrant colors for better visibility
    const colors: Record<string, { bg: string; text: string; border: string; opacity?: string }> = {
      'empty': { bg: 'bg-red-500', text: 'text-white', border: 'border-red-600' },
      'home_time': { bg: 'bg-purple-100', text: 'text-purple-700', border: 'border-purple-300' },
      'dispatched': { bg: 'bg-yellow-400', text: 'text-gray-900', border: 'border-yellow-500' },
      'loaded': { bg: 'bg-green-500', text: 'text-white', border: 'border-green-600' },
      'problem': { bg: 'bg-pink-500', text: 'text-white', border: 'border-pink-600' },
      'broken': { bg: 'bg-indigo-600', text: 'text-white', border: 'border-indigo-700' },
      'broken_down': { bg: 'bg-indigo-600', text: 'text-white', border: 'border-indigo-700' },
      'broken_loaded': { bg: 'bg-teal-500', text: 'text-white', border: 'border-teal-600' },
      'broken_on_load': { bg: 'bg-teal-500', text: 'text-white', border: 'border-teal-600' },
      'has_load': { bg: 'bg-blue-600', text: 'text-white', border: 'border-blue-700' },
      'completed': { bg: 'bg-white', text: 'text-gray-600', border: 'border-gray-300' },
      // Database statuses
      'delivered': { bg: 'bg-white', text: 'text-gray-600', border: 'border-gray-300' },
      'active': { bg: 'bg-blue-500', text: 'text-white', border: 'border-blue-600' },
      'planned': { bg: 'bg-gray-400', text: 'text-white', border: 'border-gray-500' },
      'cancelled': { bg: 'bg-gray-300', text: 'text-gray-700', border: 'border-gray-400' },
      'removed': { bg: 'bg-gray-200', text: 'text-gray-600', border: 'border-gray-300' },
      'rescheduled': { bg: 'bg-orange-400', text: 'text-white', border: 'border-orange-500' },
      'archived': { bg: 'bg-white', text: 'text-gray-600', border: 'border-gray-300' },
    };
    return colors[status?.toLowerCase()] || colors['dispatched'];
  };

  useEffect(() => {
    loadData();
    loadAvailableLoads();
  }, [currentDate]);

  // Live updates: when another user changes dispatches/loads/notes, silently
  // refresh the board in place (scroll position and search are preserved).
  // Keyed on currentDate so the refresh closure always targets the visible week.
  useEffect(() => {
    let refreshTimer: ReturnType<typeof setTimeout> | null = null;
    const scheduleSilentRefresh = () => {
      // Debounce: several rapid changes (e.g. one save touching two tables)
      // collapse into a single refresh.
      if (refreshTimer) clearTimeout(refreshTimer);
      refreshTimer = setTimeout(() => {
        loadData({ silent: true });
        loadAvailableLoads();
      }, 500);
    };

    const channel = supabase
      .channel('dispatch-board-live')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'dispatches' }, scheduleSilentRefresh)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'loads' }, scheduleSilentRefresh)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'load_notes' }, scheduleSilentRefresh)
      .subscribe();

    return () => {
      if (refreshTimer) clearTimeout(refreshTimer);
      supabase.removeChannel(channel);
    };
  }, [currentDate]);

  useEffect(() => () => {
    if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (datePickerRef.current && !datePickerRef.current.contains(event.target as Node)) {
        setShowDatePicker(false);
      }
    };

    if (showDatePicker) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDatePicker]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (companyFilterRef.current && !companyFilterRef.current.contains(event.target as Node)) {
        setShowCompanyFilter(false);
      }
    };

    if (showCompanyFilter) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCompanyFilter]);

  // Companies + each driver's company, loaded once. Kept outside loadData so
  // the filter also works on snapshot (past) weeks, whose rows carry no
  // company of their own.
  useEffect(() => {
    (async () => {
      const { data: companiesData } = await supabase
        .from('companies')
        .select('id, name, code')
        .eq('is_active', true)
        .order('name');
      setCompanies((companiesData || []) as { id: string; name: string; code: string }[]);

      const driverCompanies = await fetchAllRows<any>(() => supabase
        .from('drivers')
        .select('id, company_id'));
      setCompanyByDriverId(new Map(
        driverCompanies.filter(d => d.company_id).map(d => [d.id as string, d.company_id as string])
      ));
    })();
  }, []);

  async function loadAvailableLoads() {
    try {
      const { data: loadsData, error } = await supabase
        .from('loads')
        .select('id, load_number, pickup_city, pickup_state, delivery_city, delivery_state, pickup_date, delivery_date, status, total_rate, total_miles, weight')
        .in('status', ['available', 'pending', 'booked'])
        .order('delivery_date', { ascending: true });
      
      if (error) throw error;
      setAvailableLoads(loadsData || []);
    } catch (error) {
      console.error('Error loading available loads:', error);
    }
  }

  const dispatcherOrder = [
    'Mark', 'Nick', 'Alem', 'Josh', 'Ross', 'Nemo', 'Brooklyn',
    'Sam', 'David', 'Jamie', 'Alex', 'Lilly', 'Ben York',
    'Blaze', 'Bob', 'Ned', 'Hans', 'Boris', 'Ian York', 'Pete'
  ];

  const sortDispatcherNames = (names: string[]) => {
    return names.sort((nameA, nameB) => {
      const indexA = dispatcherOrder.findIndex(d => nameA.toLowerCase().includes(d.toLowerCase()));
      const indexB = dispatcherOrder.findIndex(d => nameB.toLowerCase().includes(d.toLowerCase()));
      if (indexA !== -1 && indexB !== -1) return indexA - indexB;
      if (indexA !== -1) return -1;
      if (indexB !== -1) return 1;
      return nameA.localeCompare(nameB);
    });
  };

  // Load and transform dispatches for a given week range.
  async function loadDispatches(weekStart: Date, weekEnd: Date): Promise<Dispatch[]> {
    const dispatchesData = await fetchAllRows<any>(() => supabase
      .from('dispatches')
      .select('id, driver_id, truck_id, dispatch_date, dispatch_number, status, load_id, notes, empty_miles, driver_pay, loads!dispatches_load_id_fkey(id, load_number, status, carrier, invoice_sent, created_by, pickup_date, delivery_date, rescheduled_delivery_date, total_rate, total_miles, load_stops(facility_name, street, city, state, zip_code, stop_type, stop_order, date, time_from, time_to), load_notes(id, note, created_at, created_by, profiles(full_name))), trucks(unit_number, dispatcher_id)')
      .neq('status', 'removed'));

    return dispatchesData
      .filter((dispatch: any) => {
        const load = dispatch.loads;
        if (load?.status === 'removed') return false;
        return true;
      })
      .map((dispatch: any) => {
        const load = dispatch.loads;
        const isRescheduled = !!load?.rescheduled_delivery_date;
        const pickupDate = load?.pickup_date || dispatch.dispatch_date;
        const deliveryDate = load?.delivery_date || dispatch.dispatch_date;

        const stops = load?.load_stops || [];
        const sortedStops = [...stops].sort((a: any, b: any) => (a.stop_order || 0) - (b.stop_order || 0));
        const pickupStop = sortedStops.find((s: any) => s.stop_type === 'shipper') || sortedStops[0];
        const deliveryStops = sortedStops.filter((s: any) => s.stop_type === 'consignee');
        const deliveryStop = deliveryStops.length > 0 ? deliveryStops[deliveryStops.length - 1] : sortedStops[sortedStops.length - 1];

        const actualPickupDate = pickupStop?.date && pickupStop?.time_from
          ? `${pickupStop.date.split('T')[0]}T${pickupStop.time_from}`
          : pickupStop?.date || pickupDate;
        const actualDeliveryDate = deliveryStop?.date && deliveryStop?.time_from
          ? `${deliveryStop.date.split('T')[0]}T${deliveryStop.time_from}`
          : deliveryStop?.date || deliveryDate;

        const finalDeliveryDate = isRescheduled && load?.rescheduled_delivery_date
          ? load.rescheduled_delivery_date
          : actualDeliveryDate;

        const origin = pickupStop?.city && pickupStop?.state
          ? `${pickupStop.city}, ${pickupStop.state}`
          : dispatch.notes || undefined;
        const destination = deliveryStop?.city && deliveryStop?.state
          ? `${deliveryStop.city}, ${deliveryStop.state}`
          : undefined;

        return {
          id: dispatch.id,
          driver_id: dispatch.driver_id,
          truck_id: dispatch.trucks?.unit_number || dispatch.truck_id,
          raw_truck_id: dispatch.truck_id,
          truck_dispatcher_id: dispatch.trucks?.dispatcher_id || undefined,
          load_created_by: load?.created_by || undefined,
          start_time: actualPickupDate,
          end_time: finalDeliveryDate,
          status: dispatch.status,
          load_id: dispatch.load_id || load?.id,
          load_number: load?.load_number,
          dispatch_number: dispatch.dispatch_number,
          origin,
          destination,
          load_stops: sortedStops,
          load_notes: load?.load_notes || [],
          stops: sortedStops,
          is_rescheduled: isRescheduled,
          carrier: load?.carrier,
          invoice_sent: load?.invoice_sent === true,
          total_rate: load?.total_rate || 0,
          total_miles: load?.total_miles || 0,
          empty_miles: dispatch.empty_miles || 0,
          driver_pay: dispatch.driver_pay || 0
        };
      })
      .filter((dispatch) => {
        if (dispatch.status === 'removed') return false;
        const dispatchStart = new Date(dispatch.start_time);
        const dispatchEnd = new Date(dispatch.end_time);
        return dispatchStart <= weekEnd && dispatchEnd >= weekStart;
      });
  }

  async function loadData(options?: { silent?: boolean }) {
    try {
      // Silent refresh (after a save/update) keeps the board mounted so the
      // user's scroll position and search are preserved.
      if (!options?.silent) setLoading(true);

      const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
      const weekEnd = addDays(weekStart, 7);
      const weekStartDate = format(weekStart, 'yyyy-MM-dd');

      // A dispatch belongs to THIS week if its pickup (start_time) falls inside
      // the visible Mon→next-Mon range. A load that picked up in a prior week but
      // delivers into this one belongs to the week it was picked up (matches the
      // Dispatchers Gross attribution) — so it must not create a phantom row here.
      const dispatchInWeek = (d: Dispatch): boolean => {
        if (!d.start_time) return false;
        const t = new Date(d.start_time).getTime();
        return t >= weekStart.getTime() && t <= weekEnd.getTime();
      };

      // Check if viewing a past week
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const currentWeekStart = startOfWeek(today, { weekStartsOn: 1 });
      const isPastWeek = weekStart < currentWeekStart;

      // For past weeks, try to load from snapshot first
      if (isPastWeek) {
        const { data: snapshotData, error: snapshotError } = await supabase
          .from('weekly_dispatch_snapshots')
          .select('driver_id, driver_first_name, driver_last_name, driver_phone, driver_type, dispatcher_id, dispatcher_key, dispatcher_name, truck_id, truck_number, trailer_id, trailer_number')
          .eq('week_start_date', weekStartDate);

        if (!snapshotError && snapshotData && snapshotData.length > 0) {
          console.log('[DISPATCH BOARD] Loading from snapshot for week:', weekStartDate, `(${snapshotData.length} drivers)`);

          // Build dispatchers from snapshot (includes deleted dispatchers)
          const dispatcherNames = new Set<string>();
          snapshotData.forEach((snap: any) => {
            if (snap.dispatcher_name) dispatcherNames.add(snap.dispatcher_name);
          });
          const sortedNames = sortDispatcherNames(Array.from(dispatcherNames));
          setDispatchers(sortedNames.length > 0 ? sortedNames : ['Unassigned']);

          // Build drivers from snapshot. With split rows there is one snapshot
          // row per (driver, dispatcher) — each becomes its own board row.
          const snapDispKey = (snap: any): string =>
            snap.dispatcher_key || (snap.dispatcher_id ? String(snap.dispatcher_id) : 'unassigned');
          const snapshotDrivers: Driver[] = snapshotData.map((snap: any) => ({
            id: snap.driver_id,
            rowKey: `${snap.driver_id}::${snapDispKey(snap)}`,
            dispatcher_id: snap.dispatcher_id || undefined,
            first_name: snap.driver_first_name || '',
            last_name: snap.driver_last_name || '',
            truck_number: snap.truck_number || 'No Truck',
            truck_id: snap.truck_id,
            phone: snap.driver_phone,
            status: 'active',
            dispatcher: snap.dispatcher_name || 'Unassigned',
            driver_type: snap.driver_type,
            trailer_number: snap.trailer_number,
            trailer_id: snap.trailer_id
          }));

          // Load dispatches from the dispatches table (already historical)
          const transformedDispatches = await loadDispatches(weekStart, weekEnd);

          // Resolve each dispatch's dispatcher so every board row only shows the
          // loads it was driven for (same logic as the live board / gross view).
          const driverIdsForTimeline = Array.from(new Set(
            transformedDispatches.map(d => d.driver_id).filter(Boolean)
          )) as string[];
          const { data: snapTimeline } = driverIdsForTimeline.length > 0
            ? await supabase
                .from('equipment_assignments')
                .select('driver_id, truck_id, assigned_at, released_at, trucks(dispatcher_id)')
                .in('driver_id', driverIdsForTimeline)
            : { data: [] };
          const snapTimelineByDriver = buildTimelineByDriver(snapTimeline || []);

          // Only REAL loads (load_id) attach a driver to a dispatcher. Home-Time
          // / empty entries are folded into the driver's primary row and never
          // create a row on their own.
          const realDispByDriverDisp = new Map<string, string[]>();
          const realDispatchersByDriver = new Map<string, Set<string>>();
          const realCountByDriverDisp = new Map<string, number>();
          const noLoadDispatchesByDriver = new Map<string, string[]>();
          transformedDispatches.forEach((dispatch) => {
            if (!dispatch.driver_id) return;
            if (!dispatchInWeek(dispatch)) return; // ignore prior-week spillover
            const dispId = resolveDispatchDispatcherId(dispatch, snapTimelineByDriver);
            dispatch.resolved_dispatcher_id = dispId;
            if (dispatch.load_id) {
              const set = realDispatchersByDriver.get(dispatch.driver_id) || new Set<string>();
              set.add(dispId);
              realDispatchersByDriver.set(dispatch.driver_id, set);
              const key = `${dispatch.driver_id}::${dispId}`;
              const arr = realDispByDriverDisp.get(key) || [];
              arr.push(dispatch.id);
              realDispByDriverDisp.set(key, arr);
              realCountByDriverDisp.set(key, (realCountByDriverDisp.get(key) || 0) + 1);
            } else {
              const arr = noLoadDispatchesByDriver.get(dispatch.driver_id) || [];
              arr.push(dispatch.id);
              noLoadDispatchesByDriver.set(dispatch.driver_id, arr);
            }
          });

          const primaryDispByDriver = new Map<string, string>();
          realDispatchersByDriver.forEach((set, driverId) => {
            let best: string | null = null;
            let bestN = -1;
            set.forEach((disp) => {
              const n = realCountByDriverDisp.get(`${driverId}::${disp}`) || 0;
              if (n > bestN) { bestN = n; best = disp; }
            });
            if (best) primaryDispByDriver.set(driverId, best);
          });

          // All of a driver's real-load dispatch ids (across every dispatcher),
          // used as a safety net below.
          const allRealByDriver = new Map<string, string[]>();
          realDispByDriverDisp.forEach((arr, key) => {
            const driverId = key.split('::')[0];
            const list = allRealByDriver.get(driverId) || [];
            list.push(...arr);
            allRealByDriver.set(driverId, list);
          });

          // Attribute each snapshot row's loads to its dispatcher; fold Home-Time
          // into the driver's primary row (or the only row if no real loads).
          const attributeRow = (driverId: string, dispKey: string): string[] => {
            const ids = [...(realDispByDriverDisp.get(`${driverId}::${dispKey}`) || [])];
            const primary = primaryDispByDriver.get(driverId);
            const noLoad = noLoadDispatchesByDriver.get(driverId) || [];
            if (primary ? dispKey === primary : true) ids.push(...noLoad);
            // SAFETY NET for not-yet-backfilled snapshots: the stored dispatcher
            // may not match the corrected live attribution, which would leave
            // this row empty and hide the driver's loads. If so, show all of the
            // driver's loads here so they never vanish. (After backfilling, the
            // keys match and this branch is never hit.)
            if (ids.length === 0) {
              const all = allRealByDriver.get(driverId) || [];
              if (all.length > 0) return [...all, ...noLoad];
            }
            return ids;
          };
          snapshotDrivers.forEach((row) => {
            const dispKey = (row.rowKey || '').split('::')[1] || 'unassigned';
            row.attributedDispatchIds = attributeRow(row.id, dispKey);
          });

          // Drivers who drove a REAL load this week but are NOT in the snapshot at
          // all (snapshot taken before the load, or a brand-new/terminated driver
          // never captured). Add them under whoever they drove for so a
          // dispatcher's gross is never lost. Drivers who DO have a snapshot row
          // already show their loads via the attribution + safety net above, so
          // we don't re-add them here (avoids duplicate rows pre-backfill).
          const snapshotDriverIds = new Set(snapshotDrivers.map(d => d.id));
          const missingPairs: { driverId: string; dispId: string }[] = [];
          realDispatchersByDriver.forEach((set, driverId) => {
            if (snapshotDriverIds.has(driverId)) return;
            set.forEach((dispId) => missingPairs.push({ driverId, dispId }));
          });

          if (missingPairs.length > 0) {
            console.log('[DISPATCH BOARD] Found', missingPairs.length, '(driver,dispatcher) real-load attributions not in snapshot, fetching...');
            const missingDriverIds = Array.from(new Set(missingPairs.map(p => p.driverId)));
            const { data: missingDriversData } = await supabase
              .from('drivers')
              .select('id, first_name, last_name, phone_number, is_active, driver_type, dispatcher_id, driver_status, termination_date, equipment_assignments(is_current, trucks(id, unit_number, dispatcher_id, trailers))')
              .in('id', missingDriverIds);
            const missingById = new Map((missingDriversData || []).map((d: any) => [d.id, d]));

            // Names for the resolved dispatchers (may be inactive/deleted).
            const resolvedDispIds = Array.from(new Set(missingPairs.map(p => p.dispId).filter(id => id !== 'unassigned')));
            const { data: resolvedProfiles } = resolvedDispIds.length > 0
              ? await supabase.from('profiles').select('id, full_name').in('id', resolvedDispIds)
              : { data: [] };
            const resolvedNameById = new Map((resolvedProfiles || []).map((p: any) => [p.id, p.full_name]));

            missingPairs.forEach(({ driverId, dispId }) => {
              const d: any = missingById.get(driverId);
              if (!d) return;
              const ca = d.equipment_assignments?.find((a: any) => a.is_current === true) || d.equipment_assignments?.[0];
              const truck = ca?.trucks;
              const dispatcherName = dispId === 'unassigned' ? 'Unassigned' : (resolvedNameById.get(dispId) || 'Unassigned');
              if (dispatcherName !== 'Unassigned') dispatcherNames.add(dispatcherName);
              snapshotDrivers.push({
                id: d.id,
                rowKey: `${d.id}::${dispId}`,
                dispatcher_id: dispId === 'unassigned' ? undefined : dispId,
                attributedDispatchIds: attributeRow(driverId, dispId),
                first_name: d.first_name || '',
                last_name: d.last_name || '',
                truck_number: (Array.isArray(truck) ? truck[0]?.unit_number : truck?.unit_number) || 'No Truck',
                truck_id: Array.isArray(truck) ? truck[0]?.id : truck?.id,
                phone: d.phone_number,
                status: d.is_active === false ? 'inactive' : 'active',
                driver_status: d.driver_status,
                termination_date: d.termination_date,
                dispatcher: dispatcherName,
                driver_type: d.driver_type,
                trailer_number: undefined,
                trailer_id: undefined
              });
            });
          }

          // Past weeks show ONLY drivers who actually drove an in-week load.
          // Any snapshot row that ends up with no attributed loads is an idle /
          // no-load driver (e.g. older snapshots that captured idle drivers, or a
          // driver whose only load belongs to a different week) — drop it.
          const visibleSnapshotDrivers = snapshotDrivers.filter(
            d => (d.attributedDispatchIds?.length || 0) > 0
          );

          const finalSnapNames = sortDispatcherNames(Array.from(dispatcherNames));
          setDispatchers(finalSnapNames.length > 0 ? finalSnapNames : ['Unassigned']);

          setDrivers(visibleSnapshotDrivers);
          setDispatches(transformedDispatches);
          return;
        }
        // No snapshot found for past week - fall through to live data
        console.log('[DISPATCH BOARD] No snapshot for past week:', weekStartDate, '- using live data');
      }

      // Current week (or past week with no snapshot): load live data.
      // Current/future weeks: DISPATCH roles only — an admin (e.g. a former
      // dispatcher) won't get an empty column unless they have drivers/loads
      // assigned this week (added below via the driver-to-dispatcher grouping).
      // Past weeks (no snapshot): also include admins + inactive users, so
      // people who used to dispatch still appear for the weeks they worked.
      const dispatchRoles = 'role.eq.dispatcher,role.eq.Dispatch,role.eq.Dispatcher,role.eq.Dispatch Manager';
      const roleFilter = isPastWeek
        ? `${dispatchRoles},role.eq.admin,role.eq.Admin`
        : dispatchRoles;
      let dispatchersQuery = supabase
        .from('profiles')
        .select('id, full_name, role, is_active')
        .or(roleFilter);
      if (!isPastWeek) {
        dispatchersQuery = dispatchersQuery.neq('is_active', false);
      }
      const { data: dispatchersData } = await dispatchersQuery;

      const sortedDispatchers = sortDispatcherNames(
        (dispatchersData || []).map(d => d.full_name).filter(Boolean)
      );

      setDispatchers(sortedDispatchers.length > 0 ? sortedDispatchers : ['Unassigned']);

      // Save dispatchers with IDs for reassignment dropdown
      const activeDispatchers = (dispatchersData || [])
        .filter(d => d.is_active !== false && d.full_name)
        .map(d => ({ id: d.id, full_name: d.full_name }))
        .sort((a, b) => a.full_name.localeCompare(b.full_name));
      setDispatchersList(activeDispatchers);

      const { data: driversData, error: driversError } = await supabase
        .from('drivers')
        .select('id, first_name, last_name, phone_number, status, driver_status, dispatcher_id, cdl_number, cdl_state, cdl_expiration, cdl_image_url, twic_image_url, needs_office_visit, office_visit_reason, termination_date, equipment_assignments(is_current, trucks(id, unit_number, dispatcher_id, make, model, year, trailers))')
        .neq('driver_status', 'terminated');

      if (driversError) throw driversError;

      // Load this week's dispatches FIRST. Driver→dispatcher grouping is driven
      // by who the driver actually drove loads for this week, not by their
      // current truck assignment (which changes on termination/reassignment).
      const transformedDispatches = await loadDispatches(weekStart, weekEnd);

      const loadedDriverIds = new Set((driversData || []).map((d: any) => d.id));

      // Every driver who drove a load this week. Anyone not in the active list
      // (terminated, released, reassigned) must still appear — under whoever
      // they drove for — so we pull their full records too.
      const driverIdsWithLoads = Array.from(new Set(
        transformedDispatches.map(d => d.driver_id).filter(Boolean)
      )) as string[];
      const missingDriverIds = driverIdsWithLoads.filter(id => !loadedDriverIds.has(id));

      let extraDriversData: any[] = [];
      if (missingDriverIds.length > 0) {
        const { data: extraDrivers } = await supabase
          .from('drivers')
          .select('id, first_name, last_name, phone_number, status, driver_status, dispatcher_id, cdl_number, cdl_state, cdl_expiration, cdl_image_url, twic_image_url, needs_office_visit, office_visit_reason, termination_date, equipment_assignments(is_current, trucks(id, unit_number, dispatcher_id, make, model, year, trailers))')
          .in('id', missingDriverIds);
        extraDriversData = extraDrivers || [];
      }

      const allDriversData = [...(driversData || []), ...extraDriversData];

      // --- Team drivers ---
      // Fetch active teams + members. The first member (by created_at) is the
      // "primary" the team's loads normalize onto. Fails gracefully if the team
      // tables don't exist yet.
      const teamMap = new Map<string, { teamId: string; primaryId: string; label: string; memberIds: string[] }>();
      {
        const { data: teamRows } = await supabase
          .from('driver_teams')
          .select('id, name, driver_team_members(driver_id, created_at, drivers(first_name, last_name))')
          .eq('is_active', true);
        (teamRows || []).forEach((t: any) => {
          const members = [...(t.driver_team_members || [])].sort(
            (a: any, b: any) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
          );
          if (members.length === 0) return;
          const memberIds = members.map((m: any) => m.driver_id);
          const label = members
            .map((m: any) => m.drivers ? `${m.drivers.first_name} ${m.drivers.last_name}` : '')
            .filter(Boolean)
            .join(' / ');
          const primaryId = memberIds[0];
          members.forEach((m: any) => {
            teamMap.set(m.driver_id, { teamId: t.id, primaryId, label, memberIds });
          });
        });
      }
      setTeamsByDriverId(teamMap);

      // --- Historical dispatcher resolution (mirrors DispatchersGrossView) ---
      // The dispatcher a load counts toward: 1) who booked it (loads.created_by),
      // 2) the dispatcher of the truck the driver was assigned to at dispatch
      //    time (equipment_assignments timeline), 3) the truck's current
      //    dispatcher. This keeps the board's grouping identical to the money
      //    (Dispatchers Gross) view.
      const { data: assignmentTimeline } = driverIdsWithLoads.length > 0
        ? await supabase
            .from('equipment_assignments')
            .select('driver_id, truck_id, assigned_at, released_at, trucks(dispatcher_id)')
            .in('driver_id', driverIdsWithLoads)
        : { data: [] };

      const timelineByDriver = buildTimelineByDriver(assignmentTimeline || []);

      // Split dispatches into REAL loads (have a load_id) and status-only
      // entries (Home Time / Empty — no load_id). Only real loads attach a
      // driver to a dispatcher and count toward gross. A terminated driver with
      // only Home Time must NOT appear; Home Time for an active driver is folded
      // into their primary dispatcher row so it never creates an Unassigned row.
      const realDispByDriverDisp = new Map<string, string[]>();        // `${driver}::${disp}` -> real-load dispatch ids
      const realDispatchersByDriver = new Map<string, Set<string>>();  // driver -> Set(dispatcher)
      const realCountByDriverDisp = new Map<string, number>();
      const noLoadDispatchesByDriver = new Map<string, string[]>();    // driver -> home-time/empty dispatch ids
      const noLoadDispCountByDriver = new Map<string, Map<string, number>>(); // driver -> dispatcher -> count (no-load)
      transformedDispatches.forEach((dispatch) => {
        if (!dispatch.driver_id) return;
        if (!dispatchInWeek(dispatch)) return; // ignore prior-week spillover
        const dispId = resolveDispatchDispatcherId(dispatch, timelineByDriver);
        dispatch.resolved_dispatcher_id = dispId;
        if (dispatch.load_id) {
          const set = realDispatchersByDriver.get(dispatch.driver_id) || new Set<string>();
          set.add(dispId);
          realDispatchersByDriver.set(dispatch.driver_id, set);
          const key = `${dispatch.driver_id}::${dispId}`;
          const arr = realDispByDriverDisp.get(key) || [];
          arr.push(dispatch.id);
          realDispByDriverDisp.set(key, arr);
          realCountByDriverDisp.set(key, (realCountByDriverDisp.get(key) || 0) + 1);
        } else {
          const arr = noLoadDispatchesByDriver.get(dispatch.driver_id) || [];
          arr.push(dispatch.id);
          noLoadDispatchesByDriver.set(dispatch.driver_id, arr);
          // Track the dispatcher the driver was with when this Home Time / empty
          // entry was logged, so an idle driver with no current truck still lands
          // on their last dispatcher instead of Unassigned.
          if (dispId !== 'unassigned') {
            const m = noLoadDispCountByDriver.get(dispatch.driver_id) || new Map<string, number>();
            m.set(dispId, (m.get(dispId) || 0) + 1);
            noLoadDispCountByDriver.set(dispatch.driver_id, m);
          }
        }
      });

      // Best (most frequent) dispatcher from a driver's Home Time / empty entries.
      const bestNoLoadDispatcher = (driverId: string): string | undefined => {
        const m = noLoadDispCountByDriver.get(driverId);
        if (!m) return undefined;
        let best: string | undefined;
        let bestN = -1;
        m.forEach((n, disp) => { if (n > bestN) { bestN = n; best = disp; } });
        return best;
      };

      // The dispatcher a driver primarily drove for (most real loads this week).
      const primaryDispByDriver = new Map<string, string>();
      realDispatchersByDriver.forEach((set, driverId) => {
        let best: string | null = null;
        let bestN = -1;
        set.forEach((disp) => {
          const n = realCountByDriverDisp.get(`${driverId}::${disp}`) || 0;
          if (n > bestN) { bestN = n; best = disp; }
        });
        if (best) primaryDispByDriver.set(driverId, best);
      });

      // Fetch all trailers to match with truck trailer numbers
      const { data: trailersData } = await supabase
        .from('trailers')
        .select('id, unit_number, make, year');

      // Create map by unit_number since trucks.trailers stores the trailer unit number as text
      const trailersMap = new Map(trailersData?.map(t => [t.unit_number?.toString(), t]) || []);

      // Every dispatcher id we need a name for: resolved-from-real-loads +
      // current assignment (for idle drivers).
      const dispatcherIds = new Set<string>();
      realDispatchersByDriver.forEach(set => set.forEach(dispId => {
        if (dispId !== 'unassigned') dispatcherIds.add(dispId);
      }));
      noLoadDispCountByDriver.forEach(m => m.forEach((_, dispId) => {
        if (dispId !== 'unassigned') dispatcherIds.add(dispId);
      }));
      allDriversData.forEach((driver: any) => {
        const currentAssignment = driver.equipment_assignments?.find((a: any) => a.is_current === true);
        const dispatcherId = currentAssignment?.trucks?.dispatcher_id || driver.dispatcher_id;
        if (dispatcherId) dispatcherIds.add(dispatcherId);
      });

      const dispatcherIdsArray = Array.from(dispatcherIds);
      const { data: dispatcherProfiles } = dispatcherIdsArray.length > 0
        ? await supabase.from('profiles').select('id, full_name, is_active').in('id', dispatcherIdsArray)
        : { data: [] };

      // Load-driven rows always resolve a name (the dispatcher earned that gross
      // even if now inactive). Idle drivers on the current week keep the old
      // behaviour: an inactive current dispatcher resolves to 'Unassigned'.
      const dispatcherNameById = new Map(
        (dispatcherProfiles || []).map(p => [p.id, p.full_name])
      );
      const idleDispatcherMap = new Map(
        (dispatcherProfiles || [])
          .filter(p => isPastWeek || p.is_active !== false)
          .map(p => [p.id, p.full_name])
      );

      // Ensure every dispatcher a driver actually drove a load for shows as a column.
      const headerNames = new Set<string>(sortedDispatchers);
      realDispatchersByDriver.forEach(set => set.forEach(dispId => {
        const name = dispId === 'unassigned' ? null : dispatcherNameById.get(dispId);
        if (name) headerNames.add(name);
      }));
      noLoadDispCountByDriver.forEach(m => m.forEach((_, dispId) => {
        const name = dispatcherNameById.get(dispId);
        if (name) headerNames.add(name);
      }));
      const mergedDispatchers = sortDispatcherNames(Array.from(headerNames));
      setDispatchers(mergedDispatchers.length > 0 ? mergedDispatchers : ['Unassigned']);

      const buildRow = (
        driver: any,
        opts: { rowKey: string; dispatcherId?: string; dispatcherName: string; attributedDispatchIds?: string[] }
      ): Driver => {
        const currentAssignment = driver.equipment_assignments?.find((a: any) => a.is_current === true);
        const trailerUnitNumber = currentAssignment?.trucks?.trailers?.toString();
        const trailer = trailerUnitNumber ? trailersMap.get(trailerUnitNumber) : null;
        const team = teamMap.get(driver.id);
        const isTeamPrimary = !!team && team.primaryId === driver.id;
        return {
          id: driver.id,
          rowKey: opts.rowKey,
          attributedDispatchIds: opts.attributedDispatchIds,
          team_id: isTeamPrimary ? team!.teamId : undefined,
          team_label: isTeamPrimary ? team!.label : undefined,
          first_name: driver.first_name,
          last_name: driver.last_name,
          truck_number: currentAssignment?.trucks?.unit_number || 'No Truck',
          truck_id: currentAssignment?.trucks?.id,
          phone: driver.phone_number,
          status: driver.status || 'active',
          driver_status: driver.driver_status,
          termination_date: driver.termination_date,
          dispatcher: opts.dispatcherName || 'Unassigned',
          dispatcher_id: opts.dispatcherId || undefined,
          cdl_number: driver.cdl_number,
          cdl_expiration: driver.cdl_expiration,
          cdl_state: driver.cdl_state,
          cdl_image_url: driver.cdl_image_url,
          twic_image_url: driver.twic_image_url,
          truck_make: currentAssignment?.trucks?.make,
          truck_model: currentAssignment?.trucks?.model,
          truck_year: currentAssignment?.trucks?.year,
          trailer_number: trailer?.unit_number,
          trailer_id: trailer?.id,
          trailer_make: trailer?.make,
          trailer_year: trailer?.year,
          needs_office_visit: driver.needs_office_visit || false,
          office_visit_reason: driver.office_visit_reason || null
        };
      };

      const transformedDrivers: Driver[] = [];
      allDriversData.forEach((driver: any) => {
        const realSet = realDispatchersByDriver.get(driver.id);
        const noLoadIds = noLoadDispatchesByDriver.get(driver.id) || [];
        if (realSet && realSet.size > 0) {
          // One row per dispatcher this driver actually drove a real load for.
          // Terminated/reassigned drivers stay attached to whoever they drove
          // for — exactly what the gross counting needs. Home-Time / empty
          // entries are folded into the primary dispatcher's row.
          const primary = primaryDispByDriver.get(driver.id);
          Array.from(realSet).forEach((dispId) => {
            const dispatcherName = dispId === 'unassigned'
              ? 'Unassigned'
              : (dispatcherNameById.get(dispId) || 'Unassigned');
            const ids = [...(realDispByDriverDisp.get(`${driver.id}::${dispId}`) || [])];
            if (dispId === primary) ids.push(...noLoadIds);
            transformedDrivers.push(buildRow(driver, {
              rowKey: `${driver.id}::${dispId}`,
              dispatcherId: dispId === 'unassigned' ? undefined : dispId,
              dispatcherName,
              attributedDispatchIds: ids
            }));
          });
        } else {
          // No REAL loads this week. Terminated drivers (even with Home Time) are
          // dropped entirely so the dispatcher gross stays accurate. Past weeks
          // show ONLY drivers who drove — idle drivers would just be empty rows.
          // The current/future board still lists idle ACTIVE drivers so they can
          // be dispatched.
          const isTerm = (driver.driver_status || '').toLowerCase() === 'terminated' || !!driver.termination_date;
          if (isTerm) return;
          // A non-primary team member with no loads of their own is represented by
          // the team's primary row — don't emit a duplicate idle row for them.
          const team = teamMap.get(driver.id);
          if (team && team.primaryId !== driver.id) return;
          if (isPastWeek) return;
          const currentAssignment = driver.equipment_assignments?.find((a: any) => a.is_current === true);
          // Prefer the current truck's dispatcher; if the driver has no current
          // truck (e.g. released this week, only Home Time), fall back to the
          // dispatcher they were with when the Home Time was logged, then their
          // saved dispatcher_id — anything but Unassigned when we can help it.
          const dispatcherId = currentAssignment?.trucks?.dispatcher_id
            || bestNoLoadDispatcher(driver.id)
            || driver.dispatcher_id;
          const dispatcherName = dispatcherId
            ? (idleDispatcherMap.get(dispatcherId) || dispatcherNameById.get(dispatcherId))
            : null;
          transformedDrivers.push(buildRow(driver, {
            rowKey: `${driver.id}::idle`,
            dispatcherId: dispatcherName ? dispatcherId : undefined,
            dispatcherName: dispatcherName || 'Unassigned',
            attributedDispatchIds: noLoadIds.length > 0 ? noLoadIds : undefined
          }));
        }
      });

      console.log('Total driver rows loaded:', transformedDrivers.length);

      setDrivers(transformedDrivers);
      setDispatches(transformedDispatches);

    } catch (error) {
      console.error('Error loading timeline data:', error);
      toast.error('Failed to load dispatch data');
    } finally {
      setLoading(false);
    }
  }

  const navigateDate = (direction: 'prev' | 'next') => {
    setCurrentDate(prev => direction === 'prev' ? subDays(prev, 7) : addDays(prev, 7));
  };

  const handleWeekYearChange = () => {
    const yearStart = startOfYear(new Date(selectedYear, 0, 1));
    const newDate = setWeek(yearStart, selectedWeek, { weekStartsOn: 1 });
    setCurrentDate(newDate);
    setShowDatePicker(false);
  };

  const goToToday = () => {
    const today = new Date();
    setCurrentDate(today);
    setSelectedWeek(getWeek(today, { weekStartsOn: 1 }));
    setSelectedYear(getYear(today));
  };

  const formatCurrentWeekRange = () => {
    const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
    const weekEnd = addDays(weekStart, 7);
    return `${format(weekStart, 'MMM d')} - ${format(weekEnd, 'MMM d')}`;
  };

  const getDays = () => {
    const days = [];
    const monday = startOfWeek(currentDate, { weekStartsOn: 1 });
    for (let i = 0; i < 8; i++) {
      days.push(addDays(monday, i));
    }
    return days;
  };

  const getDriverDispatches = (driverId: string, attributedDispatchIds?: string[]) => {
    // When a driver row is scoped to a single dispatcher (they drove for more
    // than one this week), only show that dispatcher's loads in the row.
    if (attributedDispatchIds) {
      const allow = new Set(attributedDispatchIds);
      return dispatches.filter(d =>
        d.driver_id === driverId && allow.has(d.id) && dispatchMatchesCompanyFilter(d, driverId)
      );
    }
    return dispatches.filter(dispatch =>
      dispatch.driver_id === driverId && dispatchMatchesCompanyFilter(dispatch, driverId)
    );
  };

  // loads.carrier holds the authority a load was booked under ("Apollo",
  // "Tesla", …); match it back to a row in `companies` by name or code.
  const companyForCarrier = (carrier?: string | null) => {
    if (!carrier) return undefined;
    const key = carrier.trim().toLowerCase();
    return companies.find(c =>
      (c.name || '').toLowerCase() === key || (c.code || '').toLowerCase() === key
    );
  };

  // A single load survives the company filter when the authority it was
  // booked under is ticked. When loads.carrier is blank or matches no row in
  // `companies`, fall back to the driver's own company: an unlabelled load
  // still ran for whoever the driver works under, so it stays visible while
  // that company is ticked instead of silently vanishing.
  const dispatchMatchesCompanyFilter = (dispatch: Dispatch, driverId: string) => {
    if (selectedCompanyIds.size === 0) return true;
    const company = companyForCarrier(dispatch.carrier);
    if (company) return selectedCompanyIds.has(company.id);
    const driverCompanyId = companyByDriverId.get(driverId);
    return !!driverCompanyId && selectedCompanyIds.has(driverCompanyId);
  };

  const companyColor = (company?: { id: string; code?: string; name?: string }) => {
    if (!company) return '#334155';
    const normalize = (value?: string) => (value || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
    const known = COMPANY_COLORS[normalize(company.code)] || COMPANY_COLORS[normalize(company.name)];
    if (known) return known;
    const index = companies.findIndex(c => c.id === company.id);
    return COMPANY_FALLBACK_COLORS[(index < 0 ? 0 : index) % COMPANY_FALLBACK_COLORS.length];
  };

  // Badge label: the company's initial, widened to two letters when two
  // companies share it (Tesla / Tmark both start with T).
  const companyInitials = (carrier?: string | null) => {
    const label = (companyForCarrier(carrier)?.name || carrier || '').trim();
    if (!label) return '';
    const first = label[0].toUpperCase();
    const shared = companies.filter(c => (c.name || '')[0]?.toUpperCase() === first).length > 1;
    return shared ? label.slice(0, 2).toUpperCase() : first;
  };

  // A driver row survives the company filter when the driver belongs to a
  // ticked company, or drove a load booked under one this week.
  const rowMatchesCompanyFilter = (driver: Driver) => {
    if (selectedCompanyIds.size === 0) return true;
    const driverCompanyId = companyByDriverId.get(driver.id);
    if (driverCompanyId && selectedCompanyIds.has(driverCompanyId)) return true;
    return getDriverDispatches(driver.id, driver.attributedDispatchIds)
      .some(d => dispatchMatchesCompanyFilter(d, driver.id));
  };

  const getDriversByDispatcher = () => {
    const grouped = drivers.filter(rowMatchesCompanyFilter).reduce((acc, driver) => {
      if (!acc[driver.dispatcher]) acc[driver.dispatcher] = [];
      acc[driver.dispatcher].push(driver);
      return acc;
    }, {} as Record<string, Driver[]>);
    
    // Only dispatchers with at least one driver row this week get a group.
    // Role-matched users with no drivers/loads (e.g. admins or inactive
    // dispatchers pulled in on past weeks) would otherwise render as empty
    // all-zero columns.
    const sortedGrouped: Record<string, Driver[]> = {};
    dispatchers.forEach(dispatcherName => {
      if (grouped[dispatcherName]?.length) sortedGrouped[dispatcherName] = grouped[dispatcherName];
    });

    // Surface leftover groups whose name isn't an active dispatcher (e.g.
    // "Unassigned" — terminated drivers whose truck was released, or truckless
    // drivers). To avoid cluttering this catch-all with every such driver, only
    // include the ones who actually have a load in the week being viewed —
    // and, when a company filter is on, one that survives it, so a row can't
    // be surfaced by a load the filter hides.
    Object.keys(grouped).forEach(groupName => {
      if (sortedGrouped[groupName]) return;
      const withLoadsThisWeek = grouped[groupName].filter(d =>
        dispatches.some(disp => disp.driver_id === d.id && dispatchMatchesCompanyFilter(disp, d.id))
      );
      if (withLoadsThisWeek.length > 0) {
        sortedGrouped[groupName] = withLoadsThisWeek;
      }
    });

    return sortedGrouped;
  };

  // A driver counts as terminated if their status says so or a termination
  // date has been recorded (some drivers are terminated via the date field
  // without driver_status being flipped).
  const isTerminated = (driver: Driver) =>
    (driver.driver_status || '').toLowerCase() === 'terminated' || !!driver.termination_date;

  // Show a colored badge for any non-active driver status (terminated,
  // inactive, on_leave, vacation...) so it's clear why a driver is unassigned.
  const getDriverStatusBadge = (driver: Driver): { label: string; className: string } | null => {
    if (isTerminated(driver)) {
      return { label: 'Terminated', className: 'bg-red-100 text-red-700' };
    }
    const status = (driver.driver_status || '').toLowerCase();
    if (!status || status === 'active') return null;
    const colors: Record<string, string> = {
      inactive: 'bg-slate-200 text-slate-700',
      on_leave: 'bg-yellow-100 text-yellow-800',
      vacation: 'bg-purple-100 text-purple-700',
    };
    return {
      label: status.replace(/_/g, ' '),
      className: colors[status] || 'bg-slate-200 text-slate-700',
    };
  };

  // The one load card a query points at, or null. Only load/dispatch numbers
  // identify a single card — a truck number can match every load of the week,
  // so that stays a driver-row match in driverMatchesSearch below.
  // Scans getDriverDispatches, not the raw `dispatches` array, so the search
  // can only land on a card that is actually on screen: matching a load the
  // company filter hides is what produced the reported "it opens the driver's
  // week but the load isn't there" symptom.
  const findDispatchMatchingSearch = (driver: Driver, query: string): Dispatch | null => {
    const q = query.trim().toLowerCase();
    if (!q) return null;
    return getDriverDispatches(driver.id, driver.attributedDispatchIds).find(d =>
      (d.load_number && d.load_number.toLowerCase().includes(q)) ||
      (d.dispatch_number && d.dispatch_number.toLowerCase().includes(q))
    ) || null;
  };

  // Match a driver against the board search box. Matches on driver name, the
  // driver's truck (unit) number, OR any of the driver's load numbers /
  // dispatch numbers — so users can search by load number (e.g. "3367430") or
  // truck number (e.g. "309") and jump to the driver. Boolean contract kept
  // for the row-highlight caller in the render.
  const driverMatchesSearch = (driver: Driver, query: string) => {
    const q = query.trim().toLowerCase();
    if (!q) return false;
    const name = `${driver.first_name} ${driver.last_name}`.toLowerCase();
    if (name.includes(q)) return true;
    // Truck number: skip the "No Truck" placeholder so it can't match text queries.
    const truckNumber = (driver.truck_number || '').toLowerCase();
    if (truckNumber && truckNumber !== 'no truck' && truckNumber.includes(q)) return true;
    if (findDispatchMatchingSearch(driver, q)) return true;
    // dispatches.truck_id holds the truck's unit number (see loadDispatches),
    // which covers past weeks where the driver has since changed trucks.
    return getDriverDispatches(driver.id, driver.attributedDispatchIds)
      .some(d => d.truck_id && String(d.truck_id).toLowerCase().includes(q));
  };

  // Find what the current search points at, expand the owning dispatcher
  // section, and scroll it into view. Shared by Enter and the Find button.
  // A load/dispatch number scrolls to that load's card; a driver name or truck
  // number keeps the old behaviour and scrolls to the driver row.
  const runBoardSearch = () => {
    const q = driverSearchQuery.trim();
    if (!q) return;

    // Prefer a specific load card; fall back to a name/truck match on the row.
    let matchingDriver: Driver | undefined;
    let matchingDispatch: Dispatch | null = null;
    for (const driver of drivers) {
      const dispatch = findDispatchMatchingSearch(driver, q);
      if (dispatch) {
        matchingDriver = driver;
        matchingDispatch = dispatch;
        break;
      }
    }
    if (!matchingDriver) matchingDriver = drivers.find(d => driverMatchesSearch(d, q));

    if (!matchingDriver) {
      toast.error(`No driver, truck or load found for "${q}"`);
      return;
    }

    const dispatcherEntry = Object.entries(getDriversByDispatcher()).find(([_, driversList]) =>
      driversList.some(d => d.id === matchingDriver.id)
    );

    if (dispatcherEntry) {
      const [dispatcherName] = dispatcherEntry;
      if (collapsedDispatchers.has(dispatcherName)) {
        const newCollapsed = new Set(collapsedDispatchers);
        newCollapsed.delete(dispatcherName);
        setCollapsedDispatchers(newCollapsed);
      }
    }

    // Read the ids out now: the closure below outlives the narrowing above.
    const focusDriverId = matchingDriver.id;
    const focusDispatchId = matchingDispatch?.id || null;

    // Briefly ring the matched card so the eye finds it inside the week.
    if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current);
    setHighlightedDispatchId(focusDispatchId);
    if (focusDispatchId) {
      highlightTimerRef.current = setTimeout(() => setHighlightedDispatchId(null), 4000);
    }

    setTimeout(() => {
      const target = (focusDispatchId && document.querySelector(`[data-dispatch-id="${focusDispatchId}"]`))
        || document.querySelector(`[data-driver-id="${focusDriverId}"]`);
      if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  };

  const getDispatcherTotals = (dispatcherDrivers: Driver[]) => {
    const dispatcherDriverIds = new Set(dispatcherDrivers.map(driver => driver.id));
    
    // Calculate week boundaries: Monday to Sunday (not including next Monday)
    const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
    const weekEnd = addDays(weekStart, 6); // Sunday is 6 days after Monday
    weekEnd.setHours(23, 59, 59, 999); // End of Sunday
    
    console.log('[WEEK FILTER] Week Start:', weekStart.toISOString(), 'Week End:', weekEnd.toISOString());
    
    const dispatcherDispatches = dispatches.filter(dispatch => {
      if (!dispatcherDriverIds.has(dispatch.driver_id) || !dispatch.load_id) {
        return false;
      }
      // Header totals must add up to the cards actually on screen.
      if (!dispatchMatchesCompanyFilter(dispatch, dispatch.driver_id)) {
        return false;
      }
      
      // Filter by pickup date within current week (Monday-Sunday)
      const pickupDate = new Date(dispatch.start_time);
      const isInWeek = pickupDate >= weekStart && pickupDate <= weekEnd;
      
      console.log('[WEEK FILTER]', dispatch.load_number, 'Pickup:', pickupDate.toISOString(), 'In Week:', isInWeek);
      
      return isInWeek;
    });

    const totalGross = dispatcherDispatches.reduce((sum, dispatch) => sum + (dispatch.total_rate || 0), 0);
    // Loaded and empty are tracked apart so the hover panel can show the split;
    // totalMiles stays the combined figure the header badge and RPM already use.
    const totalLoadedMiles = dispatcherDispatches.reduce((sum, dispatch) => sum + (dispatch.total_miles || 0), 0);
    const totalEmptyMiles = dispatcherDispatches.reduce((sum, dispatch) => sum + (dispatch.empty_miles || 0), 0);
    const totalMiles = dispatcherDispatches.reduce((sum, dispatch) => {
      const loadMiles = dispatch.total_miles || 0;
      const emptyMiles = dispatch.empty_miles || 0;
      return sum + loadMiles + emptyMiles;
    }, 0);
    const totalRpm = totalMiles > 0 ? totalGross / totalMiles : 0;

    console.log('[DISPATCHER TOTALS] Filtered Dispatches:', dispatcherDispatches.length, 'Gross:', totalGross, 'Miles:', totalMiles, 'RPM:', totalRpm);
    return { totalGross, totalMiles, totalLoadedMiles, totalEmptyMiles, totalRpm };
  };

  const getDriverTotals = (driverId: string, attributedDispatchIds?: string[]) => {
    // Calculate week boundaries: Monday to Sunday (not including next Monday)
    const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
    const weekEnd = addDays(weekStart, 6); // Sunday is 6 days after Monday
    weekEnd.setHours(23, 59, 59, 999); // End of Sunday

    const allow = attributedDispatchIds ? new Set(attributedDispatchIds) : null;
    const driverDispatches = dispatches.filter(dispatch => {
      if (dispatch.driver_id !== driverId || !dispatch.load_id) {
        return false;
      }
      // Row totals must add up to the cards actually on screen.
      if (!dispatchMatchesCompanyFilter(dispatch, dispatch.driver_id)) {
        return false;
      }
      // Scope totals to this row's dispatcher when the driver drove for several.
      if (allow && !allow.has(dispatch.id)) {
        return false;
      }

      // Filter by pickup date within current week (Monday-Sunday)
      const pickupDate = new Date(dispatch.start_time);
      return pickupDate >= weekStart && pickupDate <= weekEnd;
    });

    const totalGross = driverDispatches.reduce((sum, dispatch) => sum + (dispatch.total_rate || 0), 0);
    const totalDriverPay = driverDispatches.reduce((sum, dispatch) => sum + (dispatch.driver_pay || 0), 0);
    // Same split as getDispatcherTotals: totalMiles is unchanged (combined), the
    // two new fields only break it down for display.
    const totalLoadedMiles = driverDispatches.reduce((sum, dispatch) => sum + (dispatch.total_miles || 0), 0);
    const totalEmptyMiles = driverDispatches.reduce((sum, dispatch) => sum + (dispatch.empty_miles || 0), 0);
    const totalMiles = driverDispatches.reduce((sum, dispatch) => {
      const loadMiles = dispatch.total_miles || 0;
      const emptyMiles = dispatch.empty_miles || 0;
      return sum + loadMiles + emptyMiles;
    }, 0);
    const totalRpm = totalMiles > 0 ? totalGross / totalMiles : 0;

    return { totalGross, totalDriverPay, totalMiles, totalLoadedMiles, totalEmptyMiles, totalRpm };
  };

  function handleAddLoad(driver: Driver) {
    setSelectedDriver(driver);
    setLoadSearch('');
    setDriverPay(0);
    setSelectedDriverType(null);
    setSelectedLoad(null);
    setShowAddModal(true);
    loadAvailableLoads();
  }

  async function handleReassignDispatcher(driverId: string, newDispatcherId: string) {
    try {
      setReassigningDriverId(driverId);
      const driver = drivers.find(d => d.id === driverId);
      if (!driver) return;

      // Update driver's dispatcher_id
      const { error: driverError } = await supabase
        .from('drivers')
        .update({ dispatcher_id: newDispatcherId })
        .eq('id', driverId);

      if (driverError) throw driverError;

      // Also update the truck's dispatcher_id if driver has a truck
      if (driver.truck_id) {
        const { error: truckError } = await supabase
          .from('trucks')
          .update({ dispatcher_id: newDispatcherId })
          .eq('id', driver.truck_id);

        if (truckError) throw truckError;
      }

      const newDispatcher = dispatchersList.find(d => d.id === newDispatcherId);
      const newDispatcherName = newDispatcher?.full_name || 'Unassigned';

      // Update local state
      setDrivers(prev => prev.map(d =>
        d.id === driverId
          ? { ...d, dispatcher: newDispatcherName, dispatcher_id: newDispatcherId }
          : d
      ));

      toast.success(`${driver.first_name} ${driver.last_name} reassigned to ${newDispatcherName}`);
    } catch (error) {
      console.error('Error reassigning dispatcher:', error);
      toast.error('Failed to reassign dispatcher');
    } finally {
      setReassigningDriverId(null);
    }
  }

  async function handleReassignLoad(dispatchId: string, newDriverId: string, loadId?: string) {
    try {
      setReassigningLoad(true);
      const newDriver = drivers.find(d => d.id === newDriverId);
      if (!newDriver) return;

      // Find the new driver's currently-assigned truck so the dispatch + load
      // move to the right equipment as well.
      const { data: driverData } = await supabase
        .from('drivers')
        .select('equipment_assignments(is_current, trucks(id))')
        .eq('id', newDriverId)
        .single();

      const assignments = (driverData?.equipment_assignments as any[]) || [];
      const currentAssignment = assignments.find((a: any) => a.is_current === true) || assignments[0];
      const truckId = currentAssignment?.trucks?.id;

      // If the target driver is on a team, the load moves to the TEAM: normalize
      // onto the team's primary member and set team_id. Reassigning to a solo
      // driver clears team_id.
      const team = teamsByDriverId.get(newDriverId);
      const dispatchDriverId = team ? team.primaryId : newDriverId;

      // 1) Update the dispatch row (drives the board + dispatcher gross view).
      const dispatchPayload: any = {
        driver_id: dispatchDriverId,
        team_id: team ? team.teamId : null,
        updated_at: new Date().toISOString()
      };
      if (truckId) dispatchPayload.truck_id = truckId;

      const { error } = await supabase
        .from('dispatches')
        .update(dispatchPayload)
        .eq('id', dispatchId);

      if (error) throw error;

      // 2) Update the load row too. Accounting, statements, driver gross and
      // reporting read loads.driver_id directly, so it must match the dispatch
      // or the load would still be credited to the old driver.
      if (loadId) {
        const loadPayload: any = {
          driver_id: dispatchDriverId,
          team_id: team ? team.teamId : null,
          updated_at: new Date().toISOString()
        };
        if (truckId) loadPayload.truck_id = truckId;

        const { error: loadError } = await supabase
          .from('loads')
          .update(loadPayload)
          .eq('id', loadId);

        if (loadError) throw loadError;
      }

      toast.success(`Load reassigned to ${newDriver.first_name} ${newDriver.last_name}`);
      setShowReassignModal(false);
      setReassignSearch('');
      setReassignTargetDriverId(null);
      setShowStatusModal(false);
      setSelectedDispatch(null);
      loadData({ silent: true });
    } catch (error) {
      console.error('Error reassigning load:', error);
      toast.error('Failed to reassign load');
    } finally {
      setReassigningLoad(false);
    }
  }

  async function handleLoadSelect(load: any) {
    setSelectedLoad(load);
    if (!selectedDriver) return;
    
    const { data: driverData } = await supabase
      .from('drivers')
      .select('driver_type')
      .eq('id', selectedDriver.id)
      .single();
    
    const driverType = driverData?.driver_type;
    setSelectedDriverType(driverType || null);
    
    if (driverType === 'company_driver') {
      setDriverPay(load.total_rate || 0);
    } else {
      setDriverPay(0);
    }
  }

  async function handleAssignLoad() {
    if (!selectedLoad || !selectedDriver) return;
    
    setAssigningLoad(true);
    try {
      const { data: driverData } = await supabase
        .from('drivers')
        .select('equipment_assignments(trucks(id))')
        .eq('id', selectedDriver.id)
        .single();

      const truckId = (driverData?.equipment_assignments as any)?.[0]?.trucks?.id;

      if (!truckId) {
        toast.error('Driver does not have an assigned truck');
        setAssigningLoad(false);
        return;
      }

      const deliveryDate = selectedLoad.delivery_date ? new Date(selectedLoad.delivery_date) : new Date();
      const pickupDate = selectedLoad.pickup_date ? new Date(selectedLoad.pickup_date) : new Date();

      // If the driver is on a team, the load belongs to the TEAM: normalize the
      // dispatch onto the team's primary member and tag it with team_id so the
      // whole team's loads aggregate as one unit (board + statements).
      const team = teamsByDriverId.get(selectedDriver.id);
      const dispatchDriverId = team ? team.primaryId : selectedDriver.id;

      await supabase.from('dispatches').insert({
        driver_id: dispatchDriverId,
        team_id: team ? team.teamId : null,
        truck_id: truckId,
        load_id: selectedLoad.id,
        dispatch_date: pickupDate.toISOString(),
        status: 'active'
      });

      await supabase.from('loads').update({ status: 'dispatched', team_id: team ? team.teamId : null }).eq('id', selectedLoad.id);

      toast.success(`Load assigned successfully`);
      setShowAddModal(false);
      setSelectedLoad(null);
      setDriverPay(0);
      setSelectedDriverType(null);
      loadData({ silent: true });
    } catch (error) {
      console.error('Error assigning load:', error);
      toast.error('Failed to assign load');
    } finally {
      setAssigningLoad(false);
    }
  }

  async function handleStatusUpdate() {
    // Validate city input for empty and home_time statuses
    if (selectedStatus === 'empty' && !driverLocationCity.trim()) {
      toast.error('Please enter the driver location (City, State) for "Empty, need load" status');
      return;
    }
    if (selectedStatus === 'home_time' && !driverLocationCity.trim()) {
      toast.error('Please enter the driver location (City, State) for "Home Time" status');
      return;
    }

    setUpdatingStatus(true);
    try {
      // Save UI status directly to database (database constraint now accepts all UI statuses)
      // If we have a selectedDispatch, update existing dispatch
      if (selectedDispatch) {
        // For "completed" status, mark as "delivered" in the database
        const statusToSave = selectedStatus === 'completed' ? 'delivered' : selectedStatus;
        
        // Update dispatch status
        const { error: updateError } = await supabase
          .from('dispatches')
          .update({ 
            status: statusToSave,
            updated_at: new Date().toISOString()
          })
          .eq('id', selectedDispatch.id);

        if (updateError) {
          console.error('Error updating dispatch status:', updateError);
          throw updateError;
        }

        // If status is "completed", also update the load and dispatch status to "delivered"
        if (selectedStatus === 'completed' && selectedDispatch.load_id) {
          // Update load status to DELIVERED. Deliberately does NOT set
          // ready_for_billing: "Send to Accounting" on the load page is the
          // single action that flags the load for billing AND archives it, and
          // Dispatch/Dispatch Manager can now do it themselves. Setting the flag
          // here would swap that button for the green "Ready for Billing" badge,
          // leaving the load flagged but never archived.
          const { error: loadError } = await supabase
            .from('loads')
            .update({ 
              status: 'DELIVERED',
              actual_delivery_date: new Date().toISOString()
            })
            .eq('id', selectedDispatch.load_id);

          if (loadError) {
            console.error('Error updating load status:', loadError);
            throw loadError;
          }

          // Update dispatch status to delivered
          const { error: dispatchDeliveredError } = await supabase
            .from('dispatches')
            .update({ 
              status: 'delivered',
              updated_at: new Date().toISOString()
            })
            .eq('id', selectedDispatch.id);

          if (dispatchDeliveredError) {
            console.error('Error updating dispatch to delivered:', dispatchDeliveredError);
            throw dispatchDeliveredError;
          }
        }

        // Save note if provided
        if (statusNote.trim()) {
          const { data: { user } } = await supabase.auth.getUser();
          
          if (user) {
            const { error: noteError } = await supabase.from('load_notes').insert({
              load_id: selectedDispatch.load_id,
              note: statusNote,
              created_by: user.id
            });
            
            if (noteError) {
              console.error('Error saving note:', noteError);
            }
          }
        }
      } 
      // If we have selectedDriverForNote and selectedDay, create a new dispatch entry
      else if (selectedDriverForNote && selectedDay) {
        const driver = selectedDriverForNote;
        
        // Get driver's truck
        const { data: driverData } = await supabase
          .from('drivers')
          .select('equipment_assignments(trucks(id))')
          .eq('id', driver.id)
          .single();

        const truckId = (driverData?.equipment_assignments as any)?.[0]?.trucks?.id;

        // Create a dispatch entry for this driver-day with just status/note (no load)
        // Store the driver location and note in the notes field
        // Generate dispatch number using timestamp
        const dispatchNumber = `DIS-${Date.now()}`;

        // Combine location and note
        const combinedNotes = [driverLocationCity, statusNote].filter(Boolean).join(' - ');

        const { error: insertError } = await supabase.from('dispatches').insert({
          driver_id: driver.id,
          truck_id: truckId,
          dispatch_date: selectedDay.toISOString(),
          dispatch_number: dispatchNumber,
          status: selectedStatus, // Save UI status directly
          load_id: null, // No load assigned, just status/note
          notes: combinedNotes || null // Store location and note in notes field
        }).select().single();

        if (insertError) {
          console.error('Error creating dispatch entry:', insertError);
          throw insertError;
        }

        // Note: statusNote is stored in dispatch.notes field above
        // We don't create a load_note entry because load_id is required (cannot be null)
      }

      toast.success('Status updated successfully');
      setShowStatusModal(false);
      setSelectedDispatch(null);
      setSelectedDriverForNote(null);
      setSelectedDay(null);
      setStatusNote('');
      setDriverLocationCity('');
      loadData({ silent: true });
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    } finally {
      setUpdatingStatus(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div 
      className="h-full bg-gradient-to-br from-gray-50 to-blue-50"
      onClick={() => {
        if (pinnedDriver) setPinnedDriver(null);
      }}
    >
      <div className="rounded-lg shadow-md p-3 text-white relative overflow-hidden" style={{ background: 'linear-gradient(to right bottom, rgb(49, 46, 129), rgb(30, 58, 138))' }}>
        <div className="absolute inset-0 bg-grid-blue-400/[0.05] bg-[size:20px_20px]"></div>
        <div className="relative flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg p-1.5">
              <Calendar className="h-4 w-4" />
            </div>
            <div>
              <h1 className="text-base font-bold text-white">Dispatch Board</h1>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 bg-white/5 px-3 py-1.5 rounded-lg">
              <User className="h-4 w-4 text-slate-300" />
              <div className="flex flex-col">
                <span className="text-xs font-semibold text-white">{Object.values(getDriversByDispatcher()).flat().length}</span>
                <span className="text-[9px] text-slate-400">Drivers</span>
              </div>
            </div>
            <div className="flex items-center gap-2 bg-white/5 px-3 py-1.5 rounded-lg">
              <Users className="h-4 w-4 text-slate-300" />
              <div className="flex flex-col">
                <span className="text-xs font-semibold text-white">{Object.keys(getDriversByDispatcher()).length}</span>
                <span className="text-[9px] text-slate-400">Dispatchers</span>
              </div>
            </div>
            <div className="flex items-center gap-2 bg-white/5 px-3 py-1.5 rounded-lg">
              <Package className="h-4 w-4 text-slate-300" />
              <div className="flex flex-col">
                <span className="text-xs font-semibold text-white">{dispatches.filter(d => d.load_id && dispatchMatchesCompanyFilter(d, d.driver_id)).length}</span>
                <span className="text-[9px] text-slate-400">Loads</span>
              </div>
            </div>
            <div className="flex items-center gap-2 bg-white/5 px-3 py-1.5 rounded-lg">
              <Calendar className="h-4 w-4 text-slate-300" />
              <span className="text-xs font-semibold text-white">{format(new Date(), 'M/d/yyyy')}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="h-[calc(100%-120px)] mt-4">
        <div className="h-full bg-gradient-to-br from-gray-50 to-blue-50">
          <div className="bg-white border-b border-gray-200">
            <div className="flex items-center justify-between px-4">
              <div className="flex items-center space-x-4">
                <div className="p-2 bg-blue-600 rounded">
                  <Calendar className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h2 className="text-base font-bold text-gray-900">Dispatch Timeline</h2>
                  <div className="flex items-center space-x-2 text-xs text-gray-600 mt-0.5">
                    <Clock className="h-3 w-3" />
                    <span className="text-xs">{format(startOfWeek(currentDate, { weekStartsOn: 1 }), 'MMM d')} - {format(addDays(startOfWeek(currentDate, { weekStartsOn: 1 }), 6), 'MMM d')}</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3">
            <button onClick={goToToday} className="px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium">
              Today
            </button>
            <button onClick={() => navigateDate('prev')} className="p-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
              <ChevronLeft className="h-5 w-5 text-gray-600" />
            </button>
            <div className="relative" ref={datePickerRef}>
              <button onClick={() => setShowDatePicker(!showDatePicker)} className="px-4 py-2 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                {formatCurrentWeekRange()}
              </button>
              {showDatePicker && (
                <div className="absolute right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg p-4 z-[9999]" style={{ minWidth: '280px' }}>
                  <div className="space-y-3">
                    <div className="border-b pb-2">
                      <h3 className="text-sm font-semibold text-gray-900">Jump to Date</h3>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1.5">Week (1-53)</label>
                      <input type="number" min="1" max="53" value={selectedWeek} onChange={(e) => setSelectedWeek(parseInt(e.target.value) || 1)} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1.5">Year</label>
                      <select value={selectedYear} onChange={(e) => setSelectedYear(parseInt(e.target.value))} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded">
                        {Array.from({ length: 11 }, (_, i) => 2020 + i).map(year => <option key={year} value={year}>{year}</option>)}
                      </select>
                    </div>
                    <div className="flex gap-2 pt-2 border-t">
                      <button onClick={handleWeekYearChange} className="flex-1 px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700">Jump</button>
                      <button onClick={() => setShowDatePicker(false)} className="px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200">Cancel</button>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <button onClick={() => navigateDate('next')} className="p-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
              <ChevronRight className="h-5 w-5 text-gray-600" />
            </button>
          </div>
        </div>
        
        {/* Status Legend and Driver Search */}
        <div className="px-4 py-2 bg-gray-50 border-b border-gray-200">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-2 flex-wrap flex-1">
              <span className="text-xs font-semibold text-gray-700 mr-2">Status Legend:</span>
              <span className="px-2 py-1 bg-[#9b0f12] text-white border border-[#9b0f12] rounded text-xs">Empty</span>
              <span className="px-2 py-1 bg-[rgb(227,200,24)] text-white border border-[rgb(227,200,24)] rounded text-xs">Dispatched</span>
              <span className="px-2 py-1 bg-[#00af00] text-white border border-[#00af00] rounded text-xs">Loaded</span>
              <span className="px-2 py-1 bg-[#cd4edd] text-white border border-[#cd4edd] rounded text-xs">Problem</span>
              <span className="px-2 py-1 bg-[#1d27a0] text-white border border-[#1d27a0] rounded text-xs">Broken Down</span>
              <span className="px-2 py-1 bg-[#0cb4a3] text-white border border-[#0cb4a3] rounded text-xs">Broken on Load</span>
              <span className="px-2 py-1 bg-[#7c898f] text-white border border-[#7c898f] rounded text-xs">Has Load</span>
              <span className="px-2 py-1 bg-white text-gray-600 border border-gray-300 rounded text-xs">Completed</span>
              {/* Not a card background like the eight above — this is the marker
                  drawn on an already-completed card. #1d4ed8 is Tailwind blue-700,
                  the exact class used for the dot and the rate on the card. */}
              <span className="px-2 py-1 bg-[#1d4ed8] text-white border border-[#1d4ed8] rounded text-xs">Invoiced</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="relative" ref={companyFilterRef}>
                <button
                  onClick={() => setShowCompanyFilter(!showCompanyFilter)}
                  className={`px-3 py-1 text-xs font-medium border rounded-lg transition-colors flex items-center gap-1.5 ${
                    selectedCompanyIds.size > 0
                      ? 'bg-blue-600 text-white border-blue-600 hover:bg-blue-700'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                  title="Show only selected companies"
                >
                  <Building2 className="h-3.5 w-3.5" />
                  {selectedCompanyIds.size === 0
                    ? 'All Companies'
                    : companies.filter(c => selectedCompanyIds.has(c.id)).map(c => c.name).join(', ')}
                  <ChevronDown className="h-3 w-3" />
                </button>
                {showCompanyFilter && (
                  <div className="absolute left-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg p-2 z-[9999] min-w-[200px]">
                    <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 px-2 pb-1">Companies</div>
                    {companies.length === 0 && (
                      <div className="px-2 py-1 text-xs text-gray-500">No companies found</div>
                    )}
                    {companies.map(company => {
                      const checked = selectedCompanyIds.has(company.id);
                      return (
                        <label key={company.id} className="flex items-center gap-2 px-2 py-1 text-xs text-gray-700 hover:bg-gray-50 rounded cursor-pointer">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => {
                              const next = new Set(selectedCompanyIds);
                              if (checked) next.delete(company.id);
                              else next.add(company.id);
                              setSelectedCompanyIds(next);
                            }}
                            className="h-3.5 w-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span
                            className="inline-flex h-4 w-4 items-center justify-center rounded text-[9px] font-bold text-white"
                            style={{ backgroundColor: companyColor(company) }}
                          >
                            {companyInitials(company.name)}
                          </span>
                          {company.name}
                        </label>
                      );
                    })}
                    {selectedCompanyIds.size > 0 && (
                      <button
                        onClick={() => setSelectedCompanyIds(new Set())}
                        className="mt-1 w-full px-2 py-1 text-xs text-blue-600 hover:bg-blue-50 rounded text-left"
                      >
                        Clear filter
                      </button>
                    )}
                  </div>
                )}
              </div>
              <div className="relative">
                <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search driver, truck # or load #..."
                  title="Search by driver name, truck (unit) number, load number or dispatch number"
                  value={driverSearchQuery}
                  onChange={(e) => setDriverSearchQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      runBoardSearch();
                    }
                  }}
                  className="pl-8 pr-3 py-1 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-48"
                />
                {driverSearchQuery && (
                  <button
                    onClick={() => setDriverSearchQuery('')}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
              </div>
              <button
                onClick={runBoardSearch}
                disabled={!driverSearchQuery}
                className="px-3 py-1 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed rounded-lg transition-colors"
              >
                Find
              </button>
            </div>
          </div>
            </div>
          </div>

          <div className="relative overflow-x-auto" style={{ height: 'calc(100% - 100px)', minWidth: '1200px' }}>
        <div className="sticky top-0 z-[100] bg-white border-b border-gray-200" style={{ minWidth: '1200px' }}>
          <div className="grid grid-cols-10 gap-1 p-1 bg-gradient-to-r from-gray-50 to-blue-50" style={{ gridTemplateColumns: boardGridColumns }}>
            <div className="text-xs font-semibold text-gray-700 px-2">DRIVERS</div>
            {getDays().map((day, index) => {
              // Get today's date in Chicago timezone (CST/CDT)
              const chicagoToday = new Date().toLocaleString('en-US', { timeZone: 'America/Chicago' });
              const todayInChicago = new Date(chicagoToday);
              const isToday = format(day, 'yyyy-MM-dd') === format(todayInChicago, 'yyyy-MM-dd');
              
              return (
              <div key={index} className="text-center">
                <div className={`text-xs font-bold px-1 py-0.5 rounded ${
                  isToday ? 'bg-green-600 text-white ring-2 ring-green-400' :
                  index === 0 ? 'bg-slate-600 text-white' : 
                  index === 6 ? 'bg-indigo-600 text-white' :
                  index === 7 ? 'bg-purple-700 text-white' : 
                  'bg-gray-100 text-gray-700'
                }`}>
                  {format(day, 'EEE')}
                </div>
                <div className={`text-xs font-medium ${isToday ? 'text-green-600 font-bold' : 'text-gray-600'}`}>{format(day, 'MMM d')}</div>
              </div>
              );
            })}
            {canSeeMoney && (
            <div className="relative group/total-help text-center">
              <div className="text-xs font-bold px-1 py-0.5 rounded bg-emerald-700 text-white cursor-help">TOTAL</div>
              <div className="text-[9px] font-medium text-gray-600">gross / pay</div>
              <div className="pointer-events-none absolute right-0 top-full mt-1 z-[200] w-64 rounded-lg border border-gray-200 bg-white p-3 text-left shadow-xl opacity-0 translate-y-1 transition-all duration-150 group-hover/total-help:opacity-100 group-hover/total-help:translate-y-0">
                <div className="text-xs font-semibold text-gray-900 mb-2">Weekly totals for this driver</div>
                <div className="flex items-start gap-2 mb-2">
                  <span className="mt-0.5 h-2 w-2 flex-shrink-0 rounded-full bg-emerald-600" />
                  <div>
                    <div className="text-[11px] font-semibold text-emerald-700">Gross</div>
                    <div className="text-[10px] leading-snug text-gray-600">
                      What the loads bill out for — the sum of every load rate the driver ran this week (Mon–Sun). This is the company's revenue, the same number that feeds Dispatchers Gross.
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <span className="mt-0.5 h-2 w-2 flex-shrink-0 rounded-full bg-blue-600" />
                  <div>
                    <div className="text-[11px] font-semibold text-blue-700">Driver pay</div>
                    <div className="text-[10px] leading-snug text-gray-600">
                      What the driver is paid out of that gross — the sum of the driver pay set on each dispatch. Shows $0 where accounting hasn't entered a pay amount yet.
                    </div>
                  </div>
                </div>
                <p className="mt-2 pt-2 border-t border-gray-100 flex items-center gap-1 text-[10px] text-gray-500">
                  <Lock className="h-3 w-3 flex-shrink-0" />
                  {moneyVisibilityNote}
                </p>
              </div>
            </div>
            )}
          </div>
        </div>

        <div className="overflow-y-auto overflow-x-visible pb-2" style={{ height: 'calc(100% - 20px)', minWidth: '1200px' }}>
          {Object.entries(getDriversByDispatcher()).map(([dispatcherName, dispatcherDrivers]) => {
            const isCollapsed = collapsedDispatchers.has(dispatcherName);
            const dispatcherTotals = getDispatcherTotals(dispatcherDrivers);
            const dispatcherDriverTotals = dispatcherDrivers.map((driver) => {
              const totals = getDriverTotals(driver.id, driver.attributedDispatchIds);
              return {
                driverName: `${driver.first_name} ${driver.last_name}`,
                ...totals,
              };
            });
            
            return (
              <div key={dispatcherName}>
                <button onClick={() => {
                  const newCollapsed = new Set(collapsedDispatchers);
                  if (isCollapsed) newCollapsed.delete(dispatcherName);
                  else newCollapsed.add(dispatcherName);
                  setCollapsedDispatchers(newCollapsed);
                }} className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white px-4 py-2 hover:from-blue-700 hover:to-blue-800 transition-colors">
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center space-x-2">
                      <ChevronDown className={`h-4 w-4 ${isCollapsed ? 'transform -rotate-90' : ''}`} />
                      <User className="h-4 w-4" />
                      <span className="font-semibold text-sm">{dispatcherName}</span>
                      <span className="text-xs bg-blue-500 px-2 py-1 rounded-full">{dispatcherDrivers.length} drivers</span>
                    </div>
                    <div className="relative group/dispatcher-totals flex items-center gap-4 text-xs">
                      {canSeeMoney && (
                        <span className="bg-blue-500/70 px-2 py-1 rounded">Gross: ${dispatcherTotals.totalGross.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      )}
                      <span className="bg-blue-500/70 px-2 py-1 rounded">Miles: {dispatcherTotals.totalMiles.toLocaleString()}</span>
                      {/* RPM is gross / miles, so it gives the gross away — same gate. */}
                      {canSeeMoney && (
                        <span className="bg-blue-500/70 px-2 py-1 rounded">RPM: ${dispatcherTotals.totalRpm.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      )}
                      <div className={`pointer-events-none absolute right-0 top-full mt-2 ${canSeeMoney ? 'w-[520px]' : 'w-[320px]'} rounded-lg border border-blue-200 bg-white p-3 text-slate-800 shadow-lg opacity-0 translate-y-1 transition-all duration-150 group-hover/dispatcher-totals:opacity-100 group-hover/dispatcher-totals:translate-y-0 z-30`}>
                        <div className="text-xs font-semibold text-slate-700 mb-2">Driver Totals</div>
                        <div className={`grid ${driverTotalsGridCols} gap-2 text-[10px] font-semibold uppercase tracking-wide text-slate-500 px-2 mb-1`}>
                          <span>Driver</span>
                          {canSeeMoney && <span>Gross</span>}
                          {/* Split spelled out once in the header so each cell can stay bare numbers. */}
                          <span>Miles<span className="block text-[9px] font-normal normal-case tracking-normal text-slate-400">loaded / empty</span></span>
                          {canSeeMoney && <span>RPM</span>}
                        </div>
                        <div className="space-y-1 max-h-64 overflow-auto">
                          {dispatcherDriverTotals.map((driverTotal) => (
                            <div key={driverTotal.driverName} className={`grid ${driverTotalsGridCols} gap-2 text-[11px] px-2 py-1 rounded bg-slate-50`}>
                              <span className="font-medium text-slate-700 truncate">{driverTotal.driverName}</span>
                              {canSeeMoney && (
                                <span className="text-green-700 font-medium">${driverTotal.totalGross.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                              )}
                              <span className="text-indigo-700 font-medium leading-tight">
                                {driverTotal.totalMiles.toLocaleString()}
                                <span className="block text-[9px] font-normal text-slate-500">
                                  {driverTotal.totalLoadedMiles.toLocaleString()} / {driverTotal.totalEmptyMiles.toLocaleString()}
                                </span>
                              </span>
                              {canSeeMoney && (
                                <span className="text-blue-700 font-medium">${driverTotal.totalRpm.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                              )}
                            </div>
                          ))}
                        </div>
                        <div className={`mt-2 pt-2 border-t border-slate-200 grid ${driverTotalsGridCols} gap-2 text-[11px] font-semibold`}>
                          <span className="text-slate-800">Total</span>
                          {canSeeMoney && (
                            <span className="text-green-700">${dispatcherTotals.totalGross.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                          )}
                          <span className="text-indigo-700 leading-tight">
                            {dispatcherTotals.totalMiles.toLocaleString()}
                            <span className="block text-[9px] font-normal text-slate-500">
                              {dispatcherTotals.totalLoadedMiles.toLocaleString()} / {dispatcherTotals.totalEmptyMiles.toLocaleString()}
                            </span>
                          </span>
                          {canSeeMoney && (
                            <span className="text-blue-700">${dispatcherTotals.totalRpm.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                          )}
                        </div>
                        {canSeeMoney && (
                          <p className="mt-2 flex items-center gap-1 text-[10px] font-normal text-slate-500">
                            <Lock className="h-3 w-3 flex-shrink-0" />
                            {moneyVisibilityNote}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </button>
                
                {!isCollapsed && dispatcherDrivers.map((driver) => {
                  // Check if driver matches search query (by name or load number)
                  const driverName = `${driver.first_name} ${driver.last_name}`.toLowerCase();
                  const matchesSearch = !driverSearchQuery || driverMatchesSearch(driver, driverSearchQuery);
                  const rowTotals = getDriverTotals(driver.id, driver.attributedDispatchIds);

                  return (
                  <div
                    key={driver.rowKey || driver.id}
                    data-driver-id={driver.id}
                    data-driver-name={driverName}
                    className={`group grid grid-cols-10 gap-1 p-1 border-b border-gray-100 hover:bg-blue-50 transition-colors ${
                      driver.needs_office_visit ? 'animate-blink-bg' : ''
                    } ${matchesSearch && driverSearchQuery ? 'bg-yellow-100 ring-2 ring-yellow-400' : ''}`} 
                    style={{ gridTemplateColumns: boardGridColumns }}
                    title={driver.needs_office_visit ? `Office Visit Required: ${driver.office_visit_reason || 'No reason provided'}` : ''}
                  >
                    <div className="flex flex-col">
                      <div
                        className="flex items-center space-x-2 relative cursor-pointer group"
                        onClick={(e) => {
                          e.stopPropagation();
                          const rect = e.currentTarget.getBoundingClientRect();
                          const viewportHeight = window.innerHeight;
                          const elementMiddle = rect.top + (rect.height / 2);
                          const isInTopHalf = elementMiddle < (viewportHeight / 2);
                          const driverIndex = drivers.findIndex(d => d.id === driver.id);
                          const shouldShowBelow = driverIndex < 3 || isInTopHalf;

                          setPinnedDriver(driver.id);
                          setHoveredDriver(null);
                          setHoverPosition({
                            x: rect.right,
                            y: shouldShowBelow ? rect.top : rect.top - 400,
                            isLeft: false,
                            isAbove: !shouldShowBelow
                          });
                        }}
                        onMouseEnter={(e) => {
                          if (!pinnedDriver) {
                            const rect = e.currentTarget.getBoundingClientRect();
                            const viewportHeight = window.innerHeight;
                            const elementMiddle = rect.top + (rect.height / 2);
                            const isInTopHalf = elementMiddle < (viewportHeight / 2);
                            const driverIndex = drivers.findIndex(d => d.id === driver.id);
                            const shouldShowBelow = driverIndex < 3 || isInTopHalf;

                            setHoveredDriver(driver.id);
                            setHoverPosition({
                              x: rect.right,
                              y: shouldShowBelow ? rect.top : rect.top - 400,
                              isLeft: false,
                              isAbove: !shouldShowBelow
                            });
                          }
                        }}
                        onMouseLeave={() => {
                          if (!pinnedDriver) {
                            setHoveredDriver(null);
                          }
                        }}
                      >
                        <div className={`p-1 rounded ${driver.status === 'active' ? 'bg-green-100' : 'bg-gray-100'}`}>
                          <User className={`h-3 w-3 ${driver.status === 'active' ? 'text-green-600' : 'text-gray-600'}`} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="font-medium text-gray-900 text-xs flex items-center gap-1">
                            <span>{driver.team_label || `${driver.first_name} ${driver.last_name}`}</span>
                            {driver.team_id && (
                              <span className="px-1 py-0 rounded text-[8px] font-bold uppercase tracking-wide flex-shrink-0 bg-teal-100 text-teal-700">TEAM</span>
                            )}
                            {(() => {
                              const badge = getDriverStatusBadge(driver);
                              if (!badge) return null;
                              return (
                                <span className={`px-1 py-0 rounded text-[8px] font-semibold uppercase tracking-wide flex-shrink-0 ${badge.className}`}>
                                  {badge.label}
                                </span>
                              );
                            })()}
                          </div>
                          <div className="text-[10px] text-gray-600">
                            <div className="flex items-center gap-1">
                              <span className="font-medium">Truck:</span>
                              <span>{driver.truck_number}</span>
                              {driver.trailer_number && (
                                <>
                                  <span className="font-medium ml-1">Trailer:</span>
                                  <span>{driver.trailer_number}</span>
                                </>
                              )}
                            </div>
                            {driver.phone && (
                              <div className="flex items-center gap-0.5">
                                <Phone className="h-2 w-2" />
                                {driver.phone}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      {(() => {
                        const isCurrentWeekView = startOfWeek(currentDate, { weekStartsOn: 1 }).getTime() === startOfWeek(new Date(), { weekStartsOn: 1 }).getTime();
                        const canReassign = isCurrentWeekView && (
                          profile?.role === 'Dispatch Manager' ||
                          profile?.role === 'Admin' ||
                          profile?.role === 'admin' ||
                          profile?.role === 'Superadmin' ||
                          profile?.role === 'superadmin'
                        );
                        if (!canReassign) return null;
                        return (
                          <div
                            className="flex items-center gap-1 mt-0.5 pl-7"
                            onClick={(e) => e.stopPropagation()}
                            onMouseEnter={(e) => e.stopPropagation()}
                          >
                            <Users className="h-2 w-2 text-gray-400 flex-shrink-0" />
                            <select
                              className="text-[9px] bg-transparent border border-gray-200 rounded px-0.5 py-0 text-gray-600 hover:border-blue-400 focus:border-blue-500 focus:outline-none cursor-pointer w-full"
                              value={driver.dispatcher_id || ''}
                              disabled={reassigningDriverId === driver.id}
                              onChange={(e) => {
                                if (e.target.value) {
                                  handleReassignDispatcher(driver.id, e.target.value);
                                }
                              }}
                            >
                              <option value="">Unassigned</option>
                              {dispatchersList.map(d => (
                                <option key={d.id} value={d.id}>{d.full_name}</option>
                              ))}
                            </select>
                          </div>
                        );
                      })()}
                    </div>

                      {getDays().map((day, dayIndex) => {
                        const dayDispatches = getDriverDispatches(driver.id, driver.attributedDispatchIds)
                          .filter(dispatch => {
                            const dispatchDateStr = dispatch.start_time ? dispatch.start_time.split('T')[0] : '';
                            const dayStr = format(day, 'yyyy-MM-dd');
                            return dispatchDateStr === dayStr;
                          })
                          .sort((a, b) => {
                            // Sort by start_time chronologically (earliest first)
                            const timeA = a.start_time || '';
                            const timeB = b.start_time || '';
                            return timeA.localeCompare(timeB);
                          });

                        return (
                          <div key={dayIndex} className={`border rounded p-0.5 min-h-[28px] relative ${
                            dayIndex === 0 ? 'border-slate-300 bg-slate-50' : 
                            dayIndex === 6 ? 'border-indigo-300 bg-indigo-50' :
                            dayIndex === 7 ? 'border-pink-300 bg-pink-50' : 
                            'border-gray-200 bg-white'
                          }`}>
                            {dayDispatches.map((dispatch) => {
                              // Use actual status color regardless of rescheduled status
                              const statusColors = getStatusColor(dispatch.status);
                              const hasMultipleLoads = dayDispatches.length > 1;
                              return (
                                <div 
                                  key={dispatch.id} 
                                  data-dispatch-id={dispatch.id}
                                  // Search highlight uses a near-black ring: it reads on every
                                  // status background and can't be mistaken for a status colour
                                  // or for the blue invoiced marker.
                                  className={`rounded-lg text-xs shadow-sm border relative group cursor-pointer hover:shadow-md transition-shadow ${statusColors.bg} ${statusColors.text} ${statusColors.border} ${statusColors.opacity || ''} ${hasMultipleLoads ? 'p-1 mb-0.5 text-[9px]' : 'p-1.5 mb-1'} ${highlightedDispatchId === dispatch.id ? 'ring-2 ring-offset-1 ring-slate-900 z-10' : ''}`}
                                  onClick={() => {
                                    setSelectedDispatch(dispatch);
                                    setSelectedStatus(dispatch.status || 'empty');
                                    setStatusNote('');
                                    setDriverLocationCity('');
                                    setShowStatusModal(true);
                                  }}
                                  onMouseEnter={(e) => {
                                    const rect = e.currentTarget.getBoundingClientRect();
                                    const mouseY = e.clientY;
                                    const mouseX = e.clientX;
                                    const viewportHeight = window.innerHeight;
                                    const viewportWidth = window.innerWidth;
                                    
                                    // Sunday is last column (index 6), next Monday is index 7
                                    const isSundayOrNextMonday = dayIndex >= 6;
                                    
                                    // Tooltip dimensions
                                    const tooltipWidth = 320;
                                    const tooltipHeight = 450;
                                    
                                    // Determine horizontal position based on available space
                                    let xPosition;
                                    let isLeft = false;
                                    
                                    if (isSundayOrNextMonday || (mouseX + tooltipWidth + 10 > viewportWidth)) {
                                      // Show on left if Sunday/Monday or not enough space on right
                                      xPosition = rect.left - tooltipWidth - 10;
                                      isLeft = true;
                                    } else {
                                      // Show on right
                                      xPosition = rect.right + 10;
                                    }
                                    
                                    // Determine vertical position - align with mouse cursor
                                    let yPosition = mouseY - 20; // Slight offset from cursor
                                    
                                    // Ensure tooltip doesn't go off screen
                                    if (yPosition + tooltipHeight > viewportHeight) {
                                      yPosition = viewportHeight - tooltipHeight - 10;
                                    }
                                    if (yPosition < 10) {
                                      yPosition = 10;
                                    }
                                    
                                    setHoveredLoadId(dispatch.id);
                                    setHoverPosition({ 
                                      x: xPosition,
                                      y: yPosition,
                                      isLeft: isLeft,
                                      isAbove: mouseY > viewportHeight / 2
                                    });
                                  }}
                                  onMouseLeave={() => setHoveredLoadId(null)}
                                >
                                  {/* Invoiced marker: an invoice PDF exists for this load.
                                      White ring so the dot survives the green/yellow/red/teal
                                      card backgrounds too, same trick as the company badge. */}
                                  {dispatch.invoice_sent && (
                                    <span
                                      className={`absolute top-0.5 right-0.5 rounded-full bg-blue-700 ring-1 ring-white/80 ${hasMultipleLoads ? 'h-1.5 w-1.5' : 'h-2 w-2'}`}
                                      title="Invoiced — an invoice has already been created for this load"
                                    />
                                  )}
                                  {/* Show load route if dispatch has a load, otherwise show status and location */}
                                  {dispatch.load_id ? (
                                    <div className="flex flex-col gap-0.5">
                                      {/* Pickup */}
                                      <div className="flex items-start gap-1">
                                        <MapPin className={`flex-shrink-0 mt-0.5 text-green-600 ${hasMultipleLoads ? 'h-2.5 w-2.5' : 'h-3 w-3'}`} />
                                        <div className="flex-1 min-w-0">
                                          <div className={`font-semibold leading-tight truncate ${hasMultipleLoads ? 'text-[9px]' : 'text-[10px]'}`}>{dispatch.origin ? dispatch.origin.toUpperCase() : 'TBD'}</div>
                                        </div>
                                      </div>
                                      {/* Delivery */}
                                      <div className="flex items-start gap-1">
                                        <MapPin className={`flex-shrink-0 mt-0.5 text-red-600 ${hasMultipleLoads ? 'h-2.5 w-2.5' : 'h-3 w-3'}`} />
                                        <div className="flex-1 min-w-0">
                                          <div className={`font-semibold leading-tight truncate ${hasMultipleLoads ? 'text-[9px]' : 'text-[10px]'}`}>{dispatch.destination ? dispatch.destination.toUpperCase() : 'TBD'}</div>
                                          <div className={`opacity-75 ${hasMultipleLoads ? 'text-[8px]' : 'text-[9px]'}`}>{formatDateTimeNoTZ(dispatch.start_time)}</div>
                                        </div>
                                      </div>
                                      {/* Company badge (load's carrier authority) + the load's real rate */}
                                      <div className="flex items-center justify-between gap-1">
                                        {dispatch.carrier ? (
                                          <span
                                            className={`flex-shrink-0 inline-flex items-center justify-center rounded font-bold text-white ring-1 ring-white/70 ${
                                              hasMultipleLoads ? 'h-3 min-w-[12px] px-0.5 text-[7px]' : 'h-3.5 min-w-[14px] px-1 text-[8px]'
                                            }`}
                                            style={{ backgroundColor: companyColor(companyForCarrier(dispatch.carrier)) }}
                                            title={companyForCarrier(dispatch.carrier)?.name || dispatch.carrier}
                                          >
                                            {companyInitials(dispatch.carrier)}
                                          </span>
                                        ) : (
                                          <span className="flex-shrink-0" />
                                        )}
                                        {canSeeMoney && !!dispatch.total_rate && (
                                          <span
                                            className={`flex-shrink-0 font-bold ${dispatch.invoice_sent ? 'text-blue-700' : 'text-gray-900'} ${hasMultipleLoads ? 'text-[8px]' : 'text-[10px]'}`}
                                            title={`Load gross $${(dispatch.total_rate || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })} (total rate billed for this load) — driver pay ${dispatch.driver_pay ? `$${dispatch.driver_pay.toLocaleString('en-US', { maximumFractionDigits: 0 })}` : 'not entered yet'}`}
                                          >
                                            ${dispatch.total_rate.toLocaleString('en-US', { maximumFractionDigits: 0 })}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  ) : (
                                    <div className={`text-center ${hasMultipleLoads ? 'py-0.5' : 'py-1'} relative`}>
                                      <button
                                        onClick={async (e) => {
                                          e.stopPropagation();
                                          if (confirm('Remove this dispatch entry?')) {
                                            try {
                                              const { error } = await supabase
                                                .from('dispatches')
                                                .delete()
                                                .eq('id', dispatch.id);
                                              
                                              if (error) throw error;
                                              
                                              toast.success('Dispatch removed');
                                              loadData({ silent: true });
                                            } catch (error) {
                                              console.error('Error removing dispatch:', error);
                                              toast.error('Failed to remove dispatch');
                                            }
                                          }
                                        }}
                                        className="absolute top-1 right-1 p-0.5 bg-black/30 hover:bg-black/50 rounded-full transition-colors"
                                        title="Remove dispatch"
                                      >
                                        <X className="h-3 w-3 text-white stroke-[3]" />
                                      </button>
                                      {/* Only show status label if not delivered/completed */}
                                      {dispatch.status !== 'delivered' && dispatch.status !== 'completed' && (
                                        <div className={`font-semibold leading-tight uppercase ${hasMultipleLoads ? 'text-[9px]' : 'text-[11px]'}`}>
                                          {dispatch.status === 'empty' ? 'Empty, need load' : 
                                           dispatch.status === 'home_time' ? 'Home Time' : 
                                           dispatch.status?.replace(/_/g, ' ')}
                                        </div>
                                      )}
                                      {dispatch.origin && (
                                        <div 
                                          className={`mt-0.5 ${hasMultipleLoads ? 'text-[8px]' : 'text-[10px]'} ${
                                            dispatch.status === 'delivered' || dispatch.status === 'completed' 
                                              ? 'text-gray-900' 
                                              : 'opacity-75'
                                          }`}
                                          title={dispatch.origin}
                                        >
                                          {dispatch.origin.length > 15 ? `${dispatch.origin.substring(0, 15)}...` : dispatch.origin}
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                            {dayDispatches.length === 0 && (
                              <div className="relative h-full min-h-[28px]">
                                {!showStatusModal && (
                                  <button 
                                    type="button"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      console.log('PenLine button clicked!', { driver: driver.first_name, day: format(day, 'EEE') });
                                      setSelectedDriverForNote(driver);
                                      setSelectedDay(day);
                                      setSelectedDispatch(null);
                                      setSelectedStatus('empty');
                                      setStatusNote('');
                                      setDriverLocationCity('');
                                      setShowStatusModal(true);
                                    }}
                                    className="absolute top-0.5 right-0.5 p-0.5 text-orange-500 hover:text-orange-700 hover:bg-orange-100 rounded transition-colors z-10 cursor-pointer" 
                                    title="Add Status/Note"
                                  >
                                    <PenLine className="h-3 w-3 pointer-events-none" />
                                  </button>
                                )}
                                <button onClick={() => handleAddLoad(driver)} className="text-center text-gray-400 text-xs hover:text-blue-600 hover:bg-blue-50 rounded p-0.5 transition-colors group w-full h-full flex items-center justify-center">
                                  <Plus className="h-2.5 w-2.5 opacity-50 group-hover:opacity-100" />
                                </button>
                              </div>
                            )}
                          </div>
                        );
                      })}

                      {/* Weekly total for this driver row: load gross + driver pay */}
                      {canSeeMoney && (
                      <div className="flex flex-col items-end justify-center pr-1 border-l border-gray-200">
                        <span
                          className="flex items-baseline gap-1 leading-tight cursor-help"
                          title={`GROSS $${rowTotals.totalGross.toLocaleString('en-US', { maximumFractionDigits: 0 })} — what the loads bill out for: the sum of every load rate ${driver.first_name} ${driver.last_name} ran this week (Mon-Sun). Company revenue, before any driver pay or deductions.`}
                        >
                          <span className="text-[8px] uppercase tracking-wide text-gray-400">gross</span>
                          <span className="text-[11px] font-bold text-emerald-700">
                            ${rowTotals.totalGross.toLocaleString('en-US', { maximumFractionDigits: 0 })}
                          </span>
                        </span>
                        <span
                          className="flex items-baseline gap-1 leading-tight cursor-help"
                          title={`DRIVER PAY $${rowTotals.totalDriverPay.toLocaleString('en-US', { maximumFractionDigits: 0 })} — what ${driver.first_name} ${driver.last_name} is paid out of that gross: the sum of the driver pay set on each of this week's dispatches. $0 means no pay amount has been entered on those loads yet.`}
                        >
                          <span className="text-[8px] uppercase tracking-wide text-gray-400">pay</span>
                          <span className="text-[10px] font-semibold text-blue-700">
                            ${rowTotals.totalDriverPay.toLocaleString('en-US', { maximumFractionDigits: 0 })}
                          </span>
                        </span>
                      </div>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}
          </div>
        </div>
      </div>

      {showAddModal && selectedDriver && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[200] p-4">
          <div className="bg-slate-900 rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col border-2 border-slate-600">
            <div className="flex items-center justify-between p-6 border-b border-slate-700 bg-gradient-to-r from-slate-800 to-slate-900 text-white">
              <div>
                <h3 className="text-xl font-bold">Assign Load</h3>
                <p className="text-sm text-slate-300">{selectedDriver.first_name} {selectedDriver.last_name}</p>
              </div>
              <button onClick={() => setShowAddModal(false)} className="p-2 hover:bg-slate-700 rounded-lg">
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="p-6 flex-1 overflow-y-auto bg-slate-800">
              <div className="mb-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-5 h-5" />
                  <input type="text" value={loadSearch} onChange={(e) => setLoadSearch(e.target.value)} placeholder="Search loads..." className="w-full pl-10 pr-4 py-2.5 bg-slate-700 border border-slate-600 text-white rounded-lg" />
                </div>
              </div>

              <div className="space-y-2">
                {availableLoads.filter(load => load.load_number?.toLowerCase().includes(loadSearch.toLowerCase())).map(load => (
                  <button key={load.id} onClick={() => handleLoadSelect(load)} className={`w-full p-3 text-left border-2 rounded-lg ${selectedLoad?.id === load.id ? 'border-blue-500 bg-blue-500/20' : 'border-slate-600 hover:border-blue-500'}`}>
                    <div className="flex justify-between">
                      <div>
                        <span className="font-bold text-white">{load.load_number}</span>
                        <div className="text-sm text-slate-300">{load.pickup_city}, {load.pickup_state} → {load.delivery_city}, {load.delivery_state}</div>
                      </div>
                      <div className="text-lg font-bold text-green-500">${load.total_rate?.toLocaleString()}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {selectedLoad && (
              <div className="px-6 py-4 border-t border-slate-700 bg-slate-800">
                <label className="block text-sm font-medium text-slate-200 mb-2">Driver Pay</label>
                <input type="number" value={driverPay || ''} onChange={(e) => setDriverPay(Number(e.target.value))} className="w-full px-4 py-2 bg-slate-700 border border-slate-600 text-white rounded-lg" />
              </div>
            )}

            <div className="px-6 py-4 border-t border-slate-700 bg-slate-900 flex justify-between">
              <button onClick={() => setShowAddModal(false)} className="px-4 py-2 border border-slate-600 text-slate-200 rounded-lg">Cancel</button>
              <button onClick={handleAssignLoad} disabled={!selectedLoad || assigningLoad} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
                {assigningLoad ? 'Assigning...' : 'Assign'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Status Update Modal */}
      {showStatusModal && (selectedDispatch || selectedDriverForNote) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[200] p-4">
          <div className="bg-slate-900 rounded-xl shadow-2xl max-w-md w-full max-h-[85vh] overflow-hidden flex flex-col border-2 border-slate-600">
            {/* Header */}
            <div className="p-3 border-b border-slate-700 flex items-center justify-between bg-gradient-to-r from-slate-800 to-slate-900 text-white">
              <div className="flex items-center gap-2">
                <div className="bg-blue-500/20 p-1.5 rounded-lg">
                  <Package className="w-4 h-4" />
                </div>
                <div>
                  <h2 className="text-base font-bold">
                    {selectedDispatch ? 'Dispatch Details' : 'Add Status/Note'}
                  </h2>
                  <div className="text-xs text-slate-300">
                    {selectedDispatch ? (
                      <span>Load: {selectedDispatch.load_number || 'N/A'}</span>
                    ) : selectedDriverForNote && selectedDay ? (
                      <span>{selectedDriverForNote.first_name} {selectedDriverForNote.last_name} - {format(selectedDay, 'EEE, MMM d')}</span>
                    ) : null}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {selectedDispatch && selectedDispatch.load_id && selectedDispatch.stops && selectedDispatch.stops.length > 0 && (
                  <button 
                    onClick={() => {
                      // Generate SMS text
                      const stops = selectedDispatch.stops || [];
                      let smsText = '';
                      
                      stops.forEach((stop: any, index: number) => {
                        const stopIcon = stop.stop_type === 'shipper' ? '📦' : '🎯';
                        const stopType = stop.stop_type === 'shipper' ? 'PICKUP' : 'DELIVERY';
                        
                        smsText += `${stopIcon} ${stopType} #${index + 1}\n`;
                        
                        // Facility name
                        if (stop.facility_name) {
                          smsText += `${stop.facility_name}\n`;
                        }
                        
                        // Address
                        smsText += `Address\n`;
                        if (stop.street) {
                          const fullAddress = stop.zip_code 
                            ? `${stop.street}, ${stop.city}, ${stop.state} ${stop.zip_code}`
                            : `${stop.street}, ${stop.city}, ${stop.state}`;
                          smsText += `${fullAddress}\n`;
                        } else {
                          smsText += `${stop.city}, ${stop.state}\n`;
                        }
                        
                        // Date & Time
                        if (stop.date && stop.time_from) {
                          smsText += `\nDate & Time\n`;
                          const formattedDateTime = formatDateTimeWithWindow(stop.date, stop.time_from, stop.time_to);
                          smsText += `${formattedDateTime}\n`;
                        }
                        
                        smsText += '\n';
                      });
                      
                      // Copy to clipboard
                      navigator.clipboard.writeText(smsText.trim()).then(() => {
                        toast.success('Load details copied to clipboard!');
                      }).catch(() => {
                        toast.error('Failed to copy to clipboard');
                      });
                    }}
                    className="p-1.5 hover:bg-slate-700 rounded-lg transition-colors"
                    title="Copy load details for SMS"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect width="14" height="14" x="8" y="8" rx="2" ry="2"/>
                      <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/>
                    </svg>
                  </button>
                )}
                <button onClick={() => setShowStatusModal(false)} className="p-1.5 hover:bg-slate-700 rounded-lg transition-colors">
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-3 bg-slate-800">
              <div className="space-y-3">
                {/* Load Details - Only show for existing dispatches with load data */}
                {selectedDispatch && selectedDispatch.load_id && (
                  <div className="bg-slate-700/50 rounded-lg p-3 border border-slate-600">
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-slate-400">Load #:</span>
                        <a 
                          href={`/loads/${selectedDispatch.load_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm font-semibold text-blue-400 hover:text-blue-300 underline cursor-pointer"
                        >
                          {selectedDispatch.load_number || 'N/A'}
                        </a>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-slate-400">Dispatch #:</span>
                        <a 
                          href={`/dispatches/${selectedDispatch.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm font-semibold text-blue-400 hover:text-blue-300 underline cursor-pointer"
                        >
                          {selectedDispatch.dispatch_number || 'N/A'}
                        </a>
                      </div>
                      {selectedDispatch.stops && selectedDispatch.stops.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-slate-600">
                          <div className="text-xs font-semibold text-slate-300 mb-2">Stops ({selectedDispatch.stops.length}):</div>
                          <div className="space-y-2">
                            {selectedDispatch.stops.map((stop: any, index: number) => (
                              <div key={index} className="flex items-start gap-2">
                                <div className="text-lg">{stop.stop_type === 'shipper' ? '📦' : '🎯'}</div>
                                <div className="flex-1">
                                  <div className="text-xs font-medium text-white">
                                    #{index + 1} {stop.city}, {stop.state}
                                  </div>
                                  <div className="text-xs text-slate-400">
                                    {stop.date && stop.time_from ? formatDateTimeWithWindow(stop.date, stop.time_from, stop.time_to) : (stop.date ? formatDateTimeNoTZ(stop.date) : 'TBD')}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Reassign Load to a Different Driver */}
                {selectedDispatch && selectedDispatch.load_id && (
                  <div>
                    <button
                      onClick={() => {
                        setReassignSearch('');
                        setReassignTargetDriverId(null);
                        setShowReassignModal(true);
                      }}
                      className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold rounded-lg transition-colors"
                    >
                      <Users className="w-4 h-4" />
                      Reassign Load to Another Driver
                    </button>
                  </div>
                )}

                {/* Status Buttons */}
                <div>
                  <label className="block text-xs font-semibold text-white mb-1.5">Update Load Status & Note</label>
                  <div className="text-xs font-medium text-slate-300 mb-2">Status:</div>
                  <div className="grid grid-cols-3 gap-1.5">
                    <button 
                      onClick={() => setSelectedStatus('empty')}
                      className={`px-2 py-1.5 rounded-lg border-2 text-xs font-medium transition-all bg-[#9b0f12] text-white border-[#9b0f12] ${selectedStatus === 'empty' ? 'ring-2 ring-offset-1 ring-blue-500' : 'opacity-60 hover:opacity-100'}`}
                    >
                      Empty, need load
                    </button>
                    <button 
                      onClick={() => setSelectedStatus('home_time')}
                      className={`px-2 py-1.5 rounded-lg border-2 text-xs font-medium transition-all bg-transparent text-white border-gray-300 ${selectedStatus === 'home_time' ? 'ring-2 ring-offset-1 ring-blue-500' : 'opacity-60 hover:opacity-100'}`}
                    >
                      Home Time
                    </button>
                    <button 
                      onClick={() => setSelectedStatus('dispatched')}
                      className={`px-2 py-1.5 rounded-lg border-2 text-xs font-medium transition-all bg-[rgb(227,200,24)] text-white border-[rgb(227,200,24)] ${selectedStatus === 'dispatched' ? 'ring-2 ring-offset-1 ring-blue-500' : 'opacity-60 hover:opacity-100'}`}
                    >
                      Dispatched
                    </button>
                    <button 
                      onClick={() => setSelectedStatus('loaded')}
                      className={`px-2 py-1.5 rounded-lg border-2 text-xs font-medium transition-all bg-[#00af00] text-white border-[#00af00] ${selectedStatus === 'loaded' ? 'ring-2 ring-offset-1 ring-blue-500' : 'opacity-60 hover:opacity-100'}`}
                    >
                      Loaded
                    </button>
                    <button 
                      onClick={() => setSelectedStatus('problem')}
                      className={`px-2 py-1.5 rounded-lg border-2 text-xs font-medium transition-all bg-[#cd4edd] text-white border-[#cd4edd] ${selectedStatus === 'problem' ? 'ring-2 ring-offset-1 ring-blue-500' : 'opacity-60 hover:opacity-100'}`}
                    >
                      Problem with the load
                    </button>
                    <button 
                      onClick={() => setSelectedStatus('broken')}
                      className={`px-2 py-1.5 rounded-lg border-2 text-xs font-medium transition-all bg-[#1d27a0] text-white border-[#1d27a0] ${selectedStatus === 'broken' ? 'ring-2 ring-offset-1 ring-blue-500' : 'opacity-60 hover:opacity-100'}`}
                    >
                      Broken down
                    </button>
                    <button 
                      onClick={() => setSelectedStatus('broken_on_load')}
                      className={`px-2 py-1.5 rounded-lg border-2 text-xs font-medium transition-all bg-[#0cb4a3] text-white border-[#0cb4a3] ${selectedStatus === 'broken_on_load' ? 'ring-2 ring-offset-1 ring-blue-500' : 'opacity-60 hover:opacity-100'}`}
                    >
                      Broken down, on a load
                    </button>
                    <button 
                      onClick={() => setSelectedStatus('has_load')}
                      className={`px-2 py-1.5 rounded-lg border-2 text-xs font-medium transition-all bg-[#7c898f] text-white border-[#7c898f] ${selectedStatus === 'has_load' ? 'ring-2 ring-offset-1 ring-blue-500' : 'opacity-60 hover:opacity-100'}`}
                    >
                      Has Load
                    </button>
                    <button 
                      onClick={() => setShowCompletionConfirm(true)}
                      className={`px-2 py-1.5 rounded-lg border-2 text-xs font-medium transition-all bg-transparent text-white border-gray-300 ${selectedStatus === 'completed' ? 'ring-2 ring-offset-1 ring-blue-500' : 'opacity-60 hover:opacity-100'}`}
                    >
                      Completed
                    </button>
                  </div>
                </div>

                {/* Driver Location (for Empty and Home Time statuses) */}
                {(selectedStatus === 'empty' || selectedStatus === 'home_time') && (
                  <div>
                    <label className="block text-xs font-semibold text-white mb-1.5">
                      Driver Location (City, State) *
                    </label>
                    <input
                      type="text"
                      value={driverLocationCity}
                      onChange={(e) => setDriverLocationCity(e.target.value)}
                      placeholder="e.g., Dallas, TX"
                      className="w-full px-2 py-1.5 bg-slate-700 border border-slate-600 text-white text-sm placeholder-slate-400 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                )}

                {/* Add Note */}
                <div>
                  <label className="block text-xs font-semibold text-white mb-1.5">Add Note</label>
                  <textarea
                    value={statusNote}
                    onChange={(e) => setStatusNote(e.target.value)}
                    placeholder="Enter a note about this dispatch..."
                    className="w-full px-2 py-1.5 bg-slate-700 border border-slate-600 text-white text-sm placeholder-slate-400 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                  />
                </div>

                {/* Notes History */}
                <div>
                  <h3 className="text-xs font-semibold text-white mb-1.5">Notes History</h3>
                  <div className="bg-slate-700/50 border border-slate-600 rounded-lg p-2 max-h-32 overflow-y-auto">
                    {selectedDispatch?.load_notes && selectedDispatch.load_notes.length > 0 ? (
                      <div className="space-y-2">
                        {selectedDispatch.load_notes.map((note: any) => (
                          <div 
                            key={note.id} 
                            className="bg-slate-800 rounded p-2 relative group"
                            title={`Added on ${new Date(note.created_at).toLocaleString()}`}
                          >
                            <button
                              onClick={async () => {
                                if (confirm('Delete this note?')) {
                                  try {
                                    const { error } = await supabase
                                      .from('load_notes')
                                      .delete()
                                      .eq('id', note.id);
                                    
                                    if (error) throw error;
                                    
                                    toast.success('Note deleted');
                                    setShowStatusModal(false);
                                    setSelectedDispatch(null);
                                    loadData({ silent: true });
                                  } catch (error) {
                                    console.error('Error deleting note:', error);
                                    toast.error('Failed to delete note');
                                  }
                                }
                              }}
                              className="absolute top-1 right-1 p-0.5 bg-red-600/80 hover:bg-red-600 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                              title="Delete note"
                            >
                              <X className="h-3 w-3 text-white" />
                            </button>
                            <p className="text-xs text-slate-300 pr-6">{note.note}</p>
                            <div className="flex items-center justify-between mt-1">
                              <p className="text-[10px] text-slate-500">{note.profiles?.full_name || 'Unknown'}</p>
                              <p className="text-[10px] text-slate-500">
                                {new Date(note.created_at).toLocaleDateString()} at {new Date(note.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-slate-400 italic">No notes yet</p>
                    )}
                  </div>
                </div>

              </div>
            </div>

            {/* Footer */}
            <div className="p-3 border-t border-slate-700 bg-slate-900 flex items-center justify-between">
              <button 
                onClick={() => setShowStatusModal(false)} 
                className="px-3 py-1.5 border border-slate-600 text-white text-sm rounded-lg hover:bg-slate-700 transition-colors font-medium"
              >
                Cancel
              </button>
              <button 
                onClick={handleStatusUpdate} 
                disabled={updatingStatus}
                className="px-4 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {updatingStatus ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reassign Load Modal - search for a driver, then confirm */}
      {showReassignModal && selectedDispatch && (() => {
        const query = reassignSearch.trim().toLowerCase();
        const candidates = drivers
          .filter(d => !isTerminated(d) && d.id !== selectedDispatch.driver_id)
          .filter(d => {
            if (!query) return true;
            const name = `${d.first_name} ${d.last_name}`.toLowerCase();
            return name.includes(query) || (d.truck_number || '').toLowerCase().includes(query);
          })
          .sort((a, b) =>
            `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`)
          );
        const selected = drivers.find(d => d.id === reassignTargetDriverId) || null;

        return (
          <div
            className="fixed inset-0 bg-black/60 flex items-center justify-center z-[250] p-4"
            onClick={() => !reassigningLoad && setShowReassignModal(false)}
          >
            <div
              className="bg-slate-900 rounded-xl shadow-2xl max-w-md w-full max-h-[80vh] overflow-hidden flex flex-col border-2 border-slate-600"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="p-3 border-b border-slate-700 flex items-center justify-between bg-gradient-to-r from-slate-800 to-slate-900 text-white">
                <div className="flex items-center gap-2">
                  <div className="bg-amber-500/20 p-1.5 rounded-lg">
                    <Users className="w-4 h-4" />
                  </div>
                  <div>
                    <h2 className="text-base font-bold">Reassign Load</h2>
                    <div className="text-xs text-slate-300">Load {selectedDispatch.load_number || 'N/A'}</div>
                  </div>
                </div>
                <button
                  onClick={() => !reassigningLoad && setShowReassignModal(false)}
                  className="p-1.5 hover:bg-slate-700 rounded-lg transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Search */}
              <div className="p-3 bg-slate-800 border-b border-slate-700">
                <div className="relative">
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <input
                    type="text"
                    autoFocus
                    placeholder="Search driver by name or truck #..."
                    value={reassignSearch}
                    onChange={(e) => setReassignSearch(e.target.value)}
                    className="w-full pl-8 pr-3 py-2 bg-slate-700 border border-slate-600 text-white text-sm placeholder-slate-400 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                  />
                </div>
              </div>

              {/* Driver list */}
              <div className="flex-1 overflow-y-auto p-2 bg-slate-800">
                {candidates.length === 0 ? (
                  <p className="text-xs text-slate-400 italic text-center py-6">No matching drivers</p>
                ) : (
                  <div className="space-y-1">
                    {candidates.map(d => (
                      <button
                        key={d.id}
                        onClick={() => setReassignTargetDriverId(d.id)}
                        className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-left transition-colors border ${
                          reassignTargetDriverId === d.id
                            ? 'bg-amber-600/20 border-amber-500'
                            : 'bg-slate-700/40 border-transparent hover:bg-slate-700'
                        }`}
                      >
                        <span className="text-sm text-white font-medium">{d.first_name} {d.last_name}</span>
                        <span className="text-xs text-slate-400">
                          {d.truck_number && d.truck_number !== 'No Truck' ? `Truck ${d.truck_number}` : 'No Truck'}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Footer / Confirm */}
              <div className="p-3 border-t border-slate-700 bg-slate-900 flex items-center justify-between gap-2">
                <button
                  onClick={() => setShowReassignModal(false)}
                  disabled={reassigningLoad}
                  className="px-3 py-1.5 border border-slate-600 text-white text-sm rounded-lg hover:bg-slate-700 transition-colors font-medium disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => reassignTargetDriverId && handleReassignLoad(selectedDispatch.id, reassignTargetDriverId, selectedDispatch.load_id)}
                  disabled={!reassignTargetDriverId || reassigningLoad}
                  className="px-4 py-1.5 bg-amber-600 text-white text-sm rounded-lg hover:bg-amber-700 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {reassigningLoad
                    ? 'Reassigning...'
                    : selected
                      ? `Confirm → ${selected.first_name} ${selected.last_name}`
                      : 'Select a driver'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Driver Hover Tooltip */}
      {(hoveredDriver || pinnedDriver) && (() => {
        const driverId = pinnedDriver || hoveredDriver;
        const driver = drivers.find(d => d.id === driverId);
        if (!driver) return null;
        
        return (
          <div 
            className={`fixed z-[300] ${pinnedDriver ? 'pointer-events-auto' : 'pointer-events-none'}`}
            style={{ 
              left: `${hoverPosition.x}px`, 
              top: `${hoverPosition.y}px`,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-white rounded-lg shadow-2xl border-2 border-blue-200 p-4 w-80">
              <div className="space-y-3">
                <div className="border-b border-gray-200 pb-2 flex items-start justify-between">
                  <div>
                    <h3 className="font-bold text-gray-900 text-sm">{driver.team_label || `${driver.first_name} ${driver.last_name}`}{driver.team_id ? ' (Team)' : ''}</h3>
                    <p className="text-xs text-gray-600">{driver.truck_number}</p>
                    {driver.phone && (
                      <p className="text-xs text-blue-600 font-medium mt-1">{driver.phone}</p>
                    )}
                  </div>
                  {pinnedDriver && (
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        setPinnedDriver(null);
                      }}
                      className="p-1 hover:bg-gray-100 rounded"
                    >
                      <X className="h-4 w-4 text-gray-500" />
                    </button>
                  )}
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    const driverDetails = `Driver: ${driver.first_name} ${driver.last_name}
Ph number: ${driver.phone || 'N/A'}
Truck Number: ${driver.truck_number}
Trailer Number: ${driver.trailer_number || 'N/A'}`;
                    navigator.clipboard.writeText(driverDetails);
                    toast.success('Driver details copied to clipboard');
                  }}
                  className="w-full flex items-center justify-center gap-2 px-3 py-2 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors font-medium"
                >
                  <Copy className="h-3 w-3" />
                  Copy Driver Details
                </button>
                
                <button
                  onClick={async (e) => {
                    e.stopPropagation();
                    try {
                      // Fetch truck VIN from database
                      const { data: driverData } = await supabase
                        .from('drivers')
                        .select('equipment_assignments(trucks(vin))')
                        .eq('id', driver.id)
                        .single();
                      
                      const assignments = driverData?.equipment_assignments as any[];
                      const truckVin = assignments?.[0]?.trucks?.vin;
                      
                      if (truckVin) {
                        navigator.clipboard.writeText(truckVin);
                        toast.success('Truck VIN copied to clipboard');
                      } else {
                        toast.error('Truck VIN not found');
                      }
                    } catch (error) {
                      console.error('Error fetching truck VIN:', error);
                      toast.error('Failed to copy truck VIN');
                    }
                  }}
                  className="w-full flex items-center justify-center gap-2 px-3 py-2 text-xs bg-purple-600 hover:bg-purple-700 text-white rounded transition-colors font-medium"
                >
                  <Copy className="h-3 w-3" />
                  Copy Truck VIN
                </button>
                
                {/* Office Visit Warning */}
                {driver.needs_office_visit && (
                  <div className="bg-red-50 border-2 border-red-200 rounded-lg p-3">
                    <div className="flex items-start gap-2">
                      <div className="text-red-600 text-lg mt-0.5">⚠️</div>
                      <div className="flex-1">
                        <div className="text-xs font-semibold text-red-900 mb-1">Office Visit Required</div>
                        <div className="text-xs text-red-700 whitespace-pre-wrap">
                          {driver.office_visit_reason || 'No reason provided'}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                <div className="space-y-2">
                  {driver.cdl_number && (
                    <div className="flex items-center gap-2">
                      <CreditCard className="h-4 w-4 text-green-600" />
                      <div className="flex-1">
                        <p className="text-xs text-gray-500">CDL Number</p>
                        <p className="text-sm font-medium text-gray-900">{driver.cdl_number}</p>
                      </div>
                    </div>
                  )}
                  {driver.cdl_expiration && (
                    <div className="flex items-center gap-2">
                      <CalendarClock className="h-4 w-4 text-orange-600" />
                      <div className="flex-1">
                        <p className="text-xs text-gray-500">CDL Expiration</p>
                        <p className="text-sm font-medium text-gray-900">{new Date(driver.cdl_expiration).toLocaleDateString()}</p>
                      </div>
                    </div>
                  )}
                  
                  {/* CDL and TWIC Download Buttons */}
                  <div className="flex gap-2 mt-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (driver.cdl_image_url) {
                          openStoredFile(driver.cdl_image_url).then(opened => {
                            if (opened) toast.success('Opening CDL document');
                            else toast.error('CDL document could not be opened');
                          });
                        } else {
                          toast.error('CDL document not uploaded');
                        }
                      }}
                      className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-xs bg-green-100 hover:bg-green-200 text-green-700 rounded transition-colors font-medium"
                    >
                      <Download className="h-3 w-3" />
                      CDL
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (driver.twic_image_url) {
                          openStoredFile(driver.twic_image_url).then(opened => {
                            if (opened) toast.success('Opening TWIC document');
                            else toast.error('TWIC document could not be opened');
                          });
                        } else {
                          toast.error('TWIC document not uploaded');
                        }
                      }}
                      className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-xs bg-blue-100 hover:bg-blue-200 text-blue-700 rounded transition-colors font-medium"
                    >
                      <Download className="h-3 w-3" />
                      TWIC
                    </button>
                  </div>
                </div>
                {(driver.truck_make || driver.truck_model) && (
                  <div className="space-y-2 pt-2 border-t border-gray-200">
                    <div className="flex items-start gap-2">
                      <Truck className="h-4 w-4 text-purple-600 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">{driver.truck_year} {driver.truck_make} {driver.truck_model}</p>
                        <p className="text-xs text-gray-600">Unit: {driver.truck_number}</p>
                        <p className="text-xs text-purple-600 font-medium uppercase">{driver.status}</p>
                        <div className="flex gap-1 mt-2">
                          <button
                            onClick={async (e) => {
                              e.stopPropagation();
                              try {
                                const { data: files } = await supabase.storage
                                  .from('company-documents')
                                  .list(`truck-photos/${driver.truck_number}`);
                                
                                if (!files || files.length === 0) {
                                  toast.error('No truck images found');
                                  return;
                                }
                                
                                // Dynamically import JSZip
                                const JSZip = (await import('jszip')).default;
                                const zip = new JSZip();
                                
                                // Download all images and add to zip
                                for (const file of files) {
                                  const { data } = await supabase.storage
                                    .from('company-documents')
                                    .download(`truck-photos/${driver.truck_number}/${file.name}`);
                                  
                                  if (data) {
                                    zip.file(file.name, data);
                                  }
                                }
                                
                                // Generate zip and download
                                const zipBlob = await zip.generateAsync({ type: 'blob' });
                                const url = URL.createObjectURL(zipBlob);
                                const a = document.createElement('a');
                                a.href = url;
                                a.download = `Truck_${driver.truck_number}_Images.zip`;
                                document.body.appendChild(a);
                                a.click();
                                document.body.removeChild(a);
                                URL.revokeObjectURL(url);
                                
                                toast.success(`Downloaded ${files.length} truck images`);
                              } catch (error) {
                                console.error('Error downloading truck images:', error);
                                toast.error('Failed to download truck images');
                              }
                            }}
                            className="flex items-center gap-1 px-2 py-1 text-[10px] bg-purple-100 hover:bg-purple-200 text-purple-700 rounded transition-colors"
                          >
                            <Download className="h-3 w-3" />
                            Images
                          </button>
                          <button
                            onClick={async (e) => {
                              e.stopPropagation();
                              try {
                                const { data: files } = await supabase.storage
                                  .from('company-documents')
                                  .list(`truck-documents/${driver.truck_number}`);
                                
                                const dotFile = files?.find(f => f.name.startsWith('dot_'));
                                if (dotFile) {
                                  const { data } = await supabase.storage
                                    .from('company-documents')
                                    .download(`truck-documents/${driver.truck_number}/${dotFile.name}`);
                                  
                                  if (data) {
                                    const url = URL.createObjectURL(data);
                                    window.open(url, '_blank');
                                  }
                                } else {
                                  toast.error('DOT inspection document not found');
                                }
                              } catch (error) {
                                console.error('Error downloading DOT:', error);
                                toast.error('Failed to download DOT document');
                              }
                            }}
                            className="flex items-center gap-1 px-2 py-1 text-[10px] bg-blue-100 hover:bg-blue-200 text-blue-700 rounded transition-colors"
                          >
                            <Download className="h-3 w-3" />
                            DOT
                          </button>
                          <button
                            onClick={async (e) => {
                              e.stopPropagation();
                              try {
                                const { data: files } = await supabase.storage
                                  .from('company-documents')
                                  .list(`truck-documents/${driver.truck_number}`);
                                
                                const regFile = files?.find(f => f.name.startsWith('registration_'));
                                if (regFile) {
                                  const { data } = await supabase.storage
                                    .from('company-documents')
                                    .download(`truck-documents/${driver.truck_number}/${regFile.name}`);
                                  
                                  if (data) {
                                    const url = URL.createObjectURL(data);
                                    window.open(url, '_blank');
                                  }
                                } else {
                                  toast.error('Registration document not found');
                                }
                              } catch (error) {
                                console.error('Error downloading registration:', error);
                                toast.error('Failed to download registration document');
                              }
                            }}
                            className="flex items-center gap-1 px-2 py-1 text-[10px] bg-green-100 hover:bg-green-200 text-green-700 rounded transition-colors"
                          >
                            <Download className="h-3 w-3" />
                            Reg
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                {driver.trailer_number && (
                  <div className="space-y-2 pt-2 border-t border-gray-200">
                    <div className="flex items-start gap-2">
                      <Truck className="h-4 w-4 text-blue-600 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-xs text-gray-500">Trailer</p>
                        <p className="text-sm font-medium text-gray-900">{driver.trailer_year} {driver.trailer_make}</p>
                        <p className="text-xs text-gray-600">Unit: {driver.trailer_number}</p>
                        <div className="flex gap-1 mt-2">
                          <button
                            onClick={async (e) => {
                              e.stopPropagation();
                              try {
                                const { data: files } = await supabase.storage
                                  .from('company-documents')
                                  .list(`trailer-photos/${driver.trailer_number}`);
                                
                                if (!files || files.length === 0) {
                                  toast.error('No trailer images found');
                                  return;
                                }
                                
                                // Dynamically import JSZip
                                const JSZip = (await import('jszip')).default;
                                const zip = new JSZip();
                                
                                // Download all images and add to zip
                                for (const file of files) {
                                  const { data } = await supabase.storage
                                    .from('company-documents')
                                    .download(`trailer-photos/${driver.trailer_number}/${file.name}`);
                                  
                                  if (data) {
                                    zip.file(file.name, data);
                                  }
                                }
                                
                                // Generate zip and download
                                const zipBlob = await zip.generateAsync({ type: 'blob' });
                                const url = URL.createObjectURL(zipBlob);
                                const a = document.createElement('a');
                                a.href = url;
                                a.download = `Trailer_${driver.trailer_number}_Images.zip`;
                                document.body.appendChild(a);
                                a.click();
                                document.body.removeChild(a);
                                URL.revokeObjectURL(url);
                                
                                toast.success(`Downloaded ${files.length} trailer images`);
                              } catch (error) {
                                console.error('Error downloading trailer images:', error);
                                toast.error('Failed to download trailer images');
                              }
                            }}
                            className="flex items-center gap-1 px-2 py-1 text-[10px] bg-purple-100 hover:bg-purple-200 text-purple-700 rounded transition-colors"
                          >
                            <Download className="h-3 w-3" />
                            Images
                          </button>
                          <button
                            onClick={async (e) => {
                              e.stopPropagation();
                              try {
                                const { data: files } = await supabase.storage
                                  .from('company-documents')
                                  .list(`trailer-documents/${driver.trailer_number}`);
                                
                                const dotFile = files?.find(f => f.name.startsWith('dot_'));
                                if (dotFile) {
                                  const { data } = await supabase.storage
                                    .from('company-documents')
                                    .download(`trailer-documents/${driver.trailer_number}/${dotFile.name}`);
                                  
                                  if (data) {
                                    const url = URL.createObjectURL(data);
                                    window.open(url, '_blank');
                                  }
                                } else {
                                  toast.error('Trailer DOT inspection document not found');
                                }
                              } catch (error) {
                                console.error('Error downloading trailer DOT:', error);
                                toast.error('Failed to download trailer DOT document');
                              }
                            }}
                            className="flex items-center gap-1 px-2 py-1 text-[10px] bg-blue-100 hover:bg-blue-200 text-blue-700 rounded transition-colors"
                          >
                            <Download className="h-3 w-3" />
                            DOT
                          </button>
                          <button
                            onClick={async (e) => {
                              e.stopPropagation();
                              try {
                                const { data: files } = await supabase.storage
                                  .from('company-documents')
                                  .list(`trailer-documents/${driver.trailer_number}`);
                                
                                const regFile = files?.find(f => f.name.startsWith('registration_'));
                                if (regFile) {
                                  const { data } = await supabase.storage
                                    .from('company-documents')
                                    .download(`trailer-documents/${driver.trailer_number}/${regFile.name}`);
                                  
                                  if (data) {
                                    const url = URL.createObjectURL(data);
                                    window.open(url, '_blank');
                                  }
                                } else {
                                  toast.error('Trailer registration document not found');
                                }
                              } catch (error) {
                                console.error('Error downloading trailer registration:', error);
                                toast.error('Failed to download trailer registration document');
                              }
                            }}
                            className="flex items-center gap-1 px-2 py-1 text-[10px] bg-green-100 hover:bg-green-200 text-green-700 rounded transition-colors"
                          >
                            <Download className="h-3 w-3" />
                            Reg
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Load Hover Tooltip */}
      {hoveredLoadId && (() => {
        const dispatch = dispatches.find(d => d.id === hoveredLoadId);
        if (!dispatch) return null;
        
        return (
          <div 
            className="fixed z-[500] pointer-events-none"
            style={{ 
              left: hoverPosition.isLeft ? `${hoverPosition.x}px` : `${hoverPosition.x + 10}px`, 
              top: `${hoverPosition.y}px`,
            }}
          >
            <div className="bg-slate-900 text-white rounded-lg shadow-2xl p-4 w-80 border border-slate-700">
              <div className="space-y-3">
                <div className="border-b border-slate-700 pb-2">
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <div className="flex items-center gap-2">
                      <Package className="h-4 w-4 text-blue-400" />
                      <span className="font-bold text-base">{dispatch.load_number || 'Load'}</span>
                    </div>
                    {dispatch.carrier && (
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-bold ${
                        dispatch.carrier === 'Apollo' ? 'bg-blue-600 text-white' :
                        dispatch.carrier === 'Tesla' ? 'bg-red-600 text-white' :
                        dispatch.carrier === 'Tmark' ? 'bg-green-600 text-white' :
                        'bg-gray-600 text-white'
                      }`}>
                        {dispatch.carrier}
                      </span>
                    )}
                  </div>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                    dispatch.status?.toLowerCase() === 'rescheduled' ? 'bg-orange-500 text-white' :
                    dispatch.status?.toLowerCase() === 'active' ? 'bg-blue-500 text-white' : 
                    dispatch.status?.toLowerCase() === 'loaded' ? 'bg-green-500 text-white' :
                    dispatch.status?.toLowerCase() === 'completed' || dispatch.status?.toLowerCase() === 'delivered' ? 'bg-gray-500 text-white' :
                    dispatch.status?.toLowerCase() === 'problem' ? 'bg-red-500 text-white' :
                    dispatch.status?.toLowerCase() === 'broken_down' ? 'bg-orange-500 text-white' :
                    dispatch.status?.toLowerCase() === 'broken_on_load' ? 'bg-red-600 text-white' :
                    dispatch.status?.toLowerCase() === 'home_time' ? 'bg-yellow-500 text-white' :
                    dispatch.status?.toLowerCase() === 'has_load' ? 'bg-indigo-500 text-white' :
                    'bg-gray-500 text-white'
                  }`}>
                    {dispatch.status?.replace(/_/g, ' ').toUpperCase()}
                  </span>
                  {dispatch.invoice_sent && (
                    <span className="ml-1.5 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-700 text-white">INVOICED</span>
                  )}
                </div>
                <div className="space-y-2">
                  {dispatch.load_stops && dispatch.load_stops.length > 0 ? (
                    <>
                      <div className="text-xs font-semibold text-slate-400 mb-2">{dispatch.load_stops.length} Stop{dispatch.load_stops.length > 1 ? 's' : ''}</div>
                      {dispatch.load_stops.map((stop: any, index: number) => {
                        const isPickup = stop.stop_type === 'shipper';
                        const isDelivery = stop.stop_type === 'consignee';
                        const pickupCount = dispatch.load_stops!.filter((s: any, i: number) => s.stop_type === 'shipper' && i <= index).length;
                        const deliveryCount = dispatch.load_stops!.filter((s: any, i: number) => s.stop_type === 'consignee' && i <= index).length;
                        
                        return (
                          <div key={index} className="flex items-start gap-2">
                            <MapPin className={`h-3 w-3 flex-shrink-0 mt-0.5 ${isPickup ? 'text-green-400' : 'text-red-400'}`} />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-0.5">
                                <span className={`text-xs font-semibold ${isPickup ? 'text-green-400' : 'text-red-400'}`}>
                                  {isPickup ? `Pickup #${pickupCount}` : isDelivery ? `Delivery #${deliveryCount}` : `Stop #${index + 1}`}
                                </span>
                              </div>
                              <p className="text-xs text-slate-300 font-medium">{stop.company_name || `${stop.city}, ${stop.state}`}</p>
                              <p className="text-xs text-slate-400">{stop.city}, {stop.state}</p>
                              <p className="text-[10px] text-slate-500">
                                {stop.date && stop.time_from ? formatDateTimeWithWindow(stop.date, stop.time_from, stop.time_to) : formatDateTimeNoTZ(stop.date)}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </>
                  ) : (
                    <>
                      {dispatch.origin && (
                        <div className="flex items-start gap-2">
                          <MapPin className="h-3 w-3 flex-shrink-0 mt-0.5 text-green-400" />
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-semibold text-green-400">Origin</p>
                            <p className="text-sm text-slate-300">{dispatch.origin}</p>
                            <p className="text-xs text-slate-400">{formatDateTimeNoTZ(dispatch.start_time)}</p>
                          </div>
                        </div>
                      )}
                      {dispatch.destination && (
                        <div className="flex items-start gap-2">
                          <MapPin className="h-3 w-3 flex-shrink-0 mt-0.5 text-red-400" />
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-semibold text-red-400">Destination</p>
                            <p className="text-sm text-slate-300">{dispatch.destination}</p>
                            <p className="text-xs text-slate-400">{formatDateTimeNoTZ(dispatch.end_time)}</p>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>

                {/* Money: what the load bills out for vs what the driver is paid */}
                {canSeeMoney && (
                <div className="border-t border-slate-700 pt-2 mt-2 grid grid-cols-2 gap-2">
                  <div className="bg-slate-800 rounded p-2">
                    <p className="text-[10px] uppercase tracking-wide text-slate-400">Load gross</p>
                    <p className="text-sm font-bold text-emerald-400">
                      ${(dispatch.total_rate || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                    <p className="text-[9px] text-slate-500">Total rate billed for this load</p>
                  </div>
                  <div className="bg-slate-800 rounded p-2">
                    <p className="text-[10px] uppercase tracking-wide text-slate-400">Driver pay</p>
                    <p className="text-sm font-bold text-blue-400">
                      ${(dispatch.driver_pay || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                    <p className="text-[9px] text-slate-500">
                      {dispatch.driver_pay ? "Driver's pay for this load" : 'Not entered yet'}
                    </p>
                  </div>
                  <p className="col-span-2 flex items-center gap-1 text-[10px] text-slate-400">
                    <Lock className="h-3 w-3 flex-shrink-0" />
                    {moneyVisibilityNote}
                  </p>
                </div>
                )}

                {/* Mileage: loaded (loads.total_miles) vs empty/deadhead (dispatches.empty_miles) */}
                <div className="border-t border-slate-700 pt-2 mt-2 grid grid-cols-2 gap-2">
                  <div className="bg-slate-800 rounded p-2">
                    <p className="text-[10px] uppercase tracking-wide text-slate-400">Loaded miles</p>
                    <p className="text-sm font-bold text-indigo-400">
                      {(dispatch.total_miles || 0).toLocaleString()}
                    </p>
                    <p className="text-[9px] text-slate-500">Miles run under load</p>
                  </div>
                  <div className="bg-slate-800 rounded p-2">
                    <p className="text-[10px] uppercase tracking-wide text-slate-400">Empty miles</p>
                    <p className="text-sm font-bold text-amber-400">
                      {(dispatch.empty_miles || 0).toLocaleString()}
                    </p>
                    <p className="text-[9px] text-slate-500">
                      {dispatch.empty_miles ? 'Deadhead on this dispatch' : 'Not entered yet'}
                    </p>
                  </div>
                  <p className="col-span-2 text-[10px] text-slate-400">
                    Total {((dispatch.total_miles || 0) + (dispatch.empty_miles || 0)).toLocaleString()} mi (loaded + empty)
                  </p>
                </div>

                {/* Notes Section */}
                {dispatch.load_notes && dispatch.load_notes.length > 0 && (
                  <div className="border-t border-slate-700 pt-2 mt-2">
                    <div className="text-xs font-semibold text-slate-400 mb-2">Notes ({dispatch.load_notes.length})</div>
                    <div className="space-y-2 max-h-32 overflow-y-auto">
                      {dispatch.load_notes.map((note: any) => (
                        <div 
                          key={note.id} 
                          className="bg-slate-800 rounded p-2"
                          title={`Added on ${new Date(note.created_at).toLocaleString()}`}
                        >
                          <p className="text-xs text-slate-300">{note.note}</p>
                          <div className="flex items-center justify-between mt-1">
                            <p className="text-[10px] text-slate-500">{note.profiles?.full_name || 'Unknown'}</p>
                            <p className="text-[10px] text-slate-500">
                              {new Date(note.created_at).toLocaleDateString()} at {new Date(note.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })()}
      </div>

      {/* Completion Confirmation Modal */}
      {showCompletionConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[300] p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-in fade-in zoom-in duration-200">
            {/* Header with warning icon */}
            <div className="bg-gradient-to-r from-amber-500 to-orange-500 p-6 text-center">
              <div className="mx-auto w-16 h-16 bg-white rounded-full flex items-center justify-center mb-3">
                <svg className="w-10 h-10 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-white">Confirm Delivery</h3>
            </div>

            {/* Content */}
            <div className="p-6">
              <p className="text-gray-700 text-center mb-2 text-lg font-semibold">
                Are you sure this load is delivered?
              </p>
              <div className="bg-amber-50 border-l-4 border-amber-500 p-4 mt-4 rounded">
                <p className="text-sm text-gray-700 leading-relaxed">
                  <span className="font-semibold">⚠️ Warning:</span> Marking this load as complete <span className="font-semibold">cannot be undone</span>. The load will be marked as delivered. To bill it, open the load and use <span className="font-semibold">Send to Accounting</span>, which also archives it.
                </p>
              </div>
            </div>

            {/* Footer with buttons */}
            <div className="bg-gray-50 px-6 py-4 flex gap-3">
              <button
                onClick={() => setShowCompletionConfirm(false)}
                className="flex-1 px-4 py-3 bg-white border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors font-semibold"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setSelectedStatus('completed');
                  setShowCompletionConfirm(false);
                }}
                className="flex-1 px-4 py-3 bg-gradient-to-r from-emerald-600 to-green-600 text-white rounded-lg hover:from-emerald-700 hover:to-green-700 transition-all font-semibold shadow-lg"
              >
                Yes, Mark as Delivered
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
